import {
    CompositeMarketStatus,
    DisplayOddStyle,
    EnumGameFrom,
    EnumResultFGLG,
    EnumScoringType,
    EnumSportsBannerAction,
    BannerPosition as PolluxBannerPosition,
    BetStatus as PolluxBetStatus,
    BrandType as PolluxBrandType,
    CashOutStatus as PolluxCashOutStatus,
    ContestStatus as PolluxContestStatus,
    DisplayTime as PolluxDisplayTime,
    ExchangeStatus as PolluxExchangeStatus,
    FantasySelectionBetStatus as PolluxFantasySelectionBetStatus,
    FantasySelectionOrderStatus as PolluxFantasySelectionOrderStatus,
    LicenseType as PolluxLicenseType,
    LiveStreamResource as PolluxLiveStreamResource,
    MainBetStatus as PolluxMainBetStatus,
    OpenTicketStatus as PolluxOpenTicketStatus,
    PolluxPlaceOrderStatus,
    PlayerType as PolluxPlayerType,
    SelectionOutcome as PolluxSelectionOutcome,
    SelectionStatus as PolluxSelectionStatus,
    SportsFeatureEvent as PolluxSportsFeatureEvent,
    SubBetStatus as PolluxSubBetStatus,
    TiEnumSportType as PolluxTiEnumSportType,
    TiEnumMarketType,
    TiEnumOddsOption,
    TiEnumPriceStyle,
    TiEnumStatementDetailStatus,
} from '@/interface/pollux';
import type { TitansoftSportsSDKSylasEnumsRecommendType as RogueRecommendType, ComSbobetBookmakerSBEnumTiEnumSportType as RogueTiEnumSportType } from '@/interface/rogue';
import {
    EnumDisplayType, EnumLanguageType,
    EnumMarketType, EnumOddsStatus, EnumOddsStyle, EnumPriceStyle, EnumSelectionStatus, EnumSportType, EnumTournamentSizeType, EnumTournamentStage, TiEnumEventStatus,
} from '@/types';

export enum SiteStyle {
    Unknown = 0,
    Asia = 1,
    Euro = 2,
}

export enum MultipleSportsZone {
    None,
    Olympic,
    AsianGames,
}

export enum SportType {
    Unknown = 0,
    Soccer = 1,
    Basketball = 2,
    Football = 3,
    IceHockey = 4,
    Badminton = 5,
    Pool = 6,
    MotorSport = 7,
    Tennis = 8,
    Baseball = 9,
    Volleyball = 10,
    Others = 11,
    Golf = 12,
    Boxing = 13,
    Cricket = 14,
    TableTennis = 15,
    Rugby = 16,
    Handball = 17,
    Cycling = 18,
    Athletics = 19,
    BeachSoccer = 20,
    Futsal = 21,
    Entertainment = 22,
    Financial = 23,
    Darts = 24,
    Olympic = 25,
    Lacrosse = 26,
    WaterPolo = 27,
    WinterSports = 28,
    Squash = 29,
    GeneralHockey = 30,
    MixedMartialArts = 31,
    ESports = 32,
    GaelicFootball = 33,
    Hurling = 34,
    MuayThai = 35,
    AussieRulesFootball = 36,
    Bandy = 37,
    WinterOlympic = 38,
    BeachVolleyball = 39,

    VirtualFootball = 5001,
    VirtualBasketball = 5002,
    VirtualRacing = 5003,
    ESportsIm = 5004,
    MuayThaiOneBx = 5005,
    VirtualNBA = 5006,

    // project defined sportType (only for display, not a valid sportType number)
    ESportsForDisplay = 9001,
    Exchange = 9002,
    FantasySelection = 9003,
    MuayThaiForDisplay = 9004,
    Racing = 23111,
}

export namespace SportType {
    const sportTypeOddsApiMapping: Record<EnumSportType, SportType> = {
        [EnumSportType.Soccer]: SportType.Soccer,
        [EnumSportType.Basketball]: SportType.Basketball,
        [EnumSportType.Football]: SportType.Football,
        [EnumSportType.Ice_Hockey]: SportType.IceHockey,
        [EnumSportType.Badminton]: SportType.Badminton,
        [EnumSportType.Pool]: SportType.Pool,
        [EnumSportType.Motor_Sport]: SportType.MotorSport,
        [EnumSportType.Tennis]: SportType.Tennis,
        [EnumSportType.Baseball]: SportType.Baseball,
        [EnumSportType.Volleyball]: SportType.Volleyball,
        [EnumSportType.Others]: SportType.Others,
        [EnumSportType.Golf]: SportType.Golf,
        [EnumSportType.Boxing]: SportType.Boxing,
        [EnumSportType.Cricket]: SportType.Cricket,
        [EnumSportType.Table_Tennis]: SportType.TableTennis,
        [EnumSportType.Rugby]: SportType.Rugby,
        [EnumSportType.Handball]: SportType.Handball,
        [EnumSportType.Cycling]: SportType.Cycling,
        [EnumSportType.Athletics]: SportType.Athletics,
        [EnumSportType.Beach_Soccer]: SportType.BeachSoccer,
        [EnumSportType.Futsal]: SportType.Futsal,
        [EnumSportType.Entertainment]: SportType.Entertainment,
        [EnumSportType.Financial]: SportType.Financial,
        [EnumSportType.Darts]: SportType.Darts,
        [EnumSportType.Olympic]: SportType.Olympic,
        [EnumSportType.Lacrosse]: SportType.Lacrosse,
        [EnumSportType.Water_Polo]: SportType.WaterPolo,
        [EnumSportType.Winter_Sports]: SportType.WinterSports,
        [EnumSportType.Squash]: SportType.Squash,
        [EnumSportType.General_Hockey]: SportType.GeneralHockey,
        [EnumSportType.Mixed_Martial_Arts]: SportType.MixedMartialArts,
        [EnumSportType.E_Sports]: SportType.ESports,
        [EnumSportType.Gaelic_Football]: SportType.GaelicFootball,
        [EnumSportType.Hurling]: SportType.Hurling,
        [EnumSportType.Muay_Thai]: SportType.MuayThai,
        [EnumSportType.Aussie_Rules_Football]: SportType.AussieRulesFootball,
        [EnumSportType.Bandy]: SportType.Bandy,
        [EnumSportType.Winter_Olympic]: SportType.WinterOlympic,
        [EnumSportType.Beach_Volleyball]: SportType.BeachVolleyball,
    };
    export function fromOddsApi(oddsApiSportName: EnumSportType): SportType {
        const oddsApiSportType = sportTypeOddsApiMapping[oddsApiSportName];
        if (oddsApiSportType === undefined) {
            throw new Error(`transform sport type to odds api error: ${oddsApiSportName}`);
        }
        return oddsApiSportType;
    }

    export function toOddsApi(sportType: SportType): EnumSportType {
        const oddsApiSportType = (Object.keys(sportTypeOddsApiMapping) as Array<EnumSportType>)
            .find(key => sportTypeOddsApiMapping[key] === sportType);
        if (oddsApiSportType === undefined) {
            throw new Error(`transform sport type to odds api error: ${sportType}`);
        }
        return oddsApiSportType;
    }

    const sportTypePolluxMapping: Record<PolluxTiEnumSportType, SportType> = {
        [PolluxTiEnumSportType.Unknown]: SportType.Unknown,
        [PolluxTiEnumSportType.Soccer]: SportType.Soccer,
        [PolluxTiEnumSportType.Basketball]: SportType.Basketball,
        [PolluxTiEnumSportType.Football]: SportType.Football,
        [PolluxTiEnumSportType.Ice_Hockey]: SportType.IceHockey,
        [PolluxTiEnumSportType.Badminton]: SportType.Badminton,
        [PolluxTiEnumSportType.Pool]: SportType.Pool,
        [PolluxTiEnumSportType.Motor_Sport]: SportType.MotorSport,
        [PolluxTiEnumSportType.Tennis]: SportType.Tennis,
        [PolluxTiEnumSportType.Baseball]: SportType.Baseball,
        [PolluxTiEnumSportType.Volleyball]: SportType.Volleyball,
        [PolluxTiEnumSportType.Others]: SportType.Others,
        [PolluxTiEnumSportType.Golf]: SportType.Golf,
        [PolluxTiEnumSportType.Boxing]: SportType.Boxing,
        [PolluxTiEnumSportType.Cricket]: SportType.Cricket,
        [PolluxTiEnumSportType.Table_Tennis]: SportType.TableTennis,
        [PolluxTiEnumSportType.Rugby]: SportType.Rugby,
        [PolluxTiEnumSportType.Handball]: SportType.Handball,
        [PolluxTiEnumSportType.Cycling]: SportType.Cycling,
        [PolluxTiEnumSportType.Athletics]: SportType.Athletics,
        [PolluxTiEnumSportType.Beach_Soccer]: SportType.BeachSoccer,
        [PolluxTiEnumSportType.Futsal]: SportType.Futsal,
        [PolluxTiEnumSportType.Entertainment]: SportType.Entertainment,
        [PolluxTiEnumSportType.Financial]: SportType.Financial,
        [PolluxTiEnumSportType.Darts]: SportType.Darts,
        [PolluxTiEnumSportType.Olympic]: SportType.Olympic,
        [PolluxTiEnumSportType.Lacrosse]: SportType.Lacrosse,
        [PolluxTiEnumSportType.Water_Polo]: SportType.WaterPolo,
        [PolluxTiEnumSportType.Winter_Sports]: SportType.WinterSports,
        [PolluxTiEnumSportType.Squash]: SportType.Squash,
        [PolluxTiEnumSportType.General_Hockey]: SportType.GeneralHockey,
        [PolluxTiEnumSportType.Mixed_Martial_Arts]: SportType.MixedMartialArts,
        [PolluxTiEnumSportType.E_Sports]: SportType.ESports,
        [PolluxTiEnumSportType.Gaelic_Football]: SportType.GaelicFootball,
        [PolluxTiEnumSportType.Hurling]: SportType.Hurling,
        [PolluxTiEnumSportType.Muay_Thai]: SportType.MuayThai,
        [PolluxTiEnumSportType.Aussie_Rules_Football]: SportType.AussieRulesFootball,
        [PolluxTiEnumSportType.Bandy]: SportType.Bandy,
        [PolluxTiEnumSportType.Winter_Olympic]: SportType.WinterOlympic,
        [PolluxTiEnumSportType.Beach_Volleyball]: SportType.BeachVolleyball,
    };
    export function fromPollux(sportType: PolluxTiEnumSportType): SportType {
        return sportTypePolluxMapping[sportType];
    }

    export function toPollux(sportType: SportType): PolluxTiEnumSportType {
        const polluxSportType = (Object.keys(sportTypePolluxMapping).map(x => parseInt(x)) as Array<PolluxTiEnumSportType>)
            .find(key => sportTypePolluxMapping[key] === sportType);
        if (polluxSportType === undefined) {
            console.error(`transform sport type to pollux error: ${sportType}`);
            return PolluxTiEnumSportType.Unknown;
        }
        return polluxSportType;
    }

    const sportTypeRouteMapping: Record<string, SportType> = {
        'football': SportType.Soccer,
        'basketball': SportType.Basketball,
        'american-football': SportType.Football,
        'ice-hockey': SportType.IceHockey,
        'badminton': SportType.Badminton,
        'pool-snooker': SportType.Pool,
        'motor-sport': SportType.MotorSport,
        'tennis': SportType.Tennis,
        'baseball': SportType.Baseball,
        'volleyball': SportType.Volleyball,
        'others': SportType.Others,
        'golf': SportType.Golf,
        'boxing': SportType.Boxing,
        'cricket': SportType.Cricket,
        'table-tennis': SportType.TableTennis,
        'rugby': SportType.Rugby,
        'handball': SportType.Handball,
        'cycling': SportType.Cycling,
        'athletics': SportType.Athletics,
        'beach-soccer': SportType.BeachSoccer,
        'futsal': SportType.Futsal,
        'special': SportType.Entertainment,
        'financial': SportType.Financial,
        'darts': SportType.Darts,
        'olympics': SportType.Olympic,
        'lacrosse': SportType.Lacrosse,
        'water-polo': SportType.WaterPolo,
        'winter-sports': SportType.WinterSports,
        'squash': SportType.Squash,
        'hockey': SportType.GeneralHockey,
        'mixed-martial-arts': SportType.MixedMartialArts,
        'e-sports': SportType.ESports,
        'gaelic-football': SportType.GaelicFootball,
        'hurling': SportType.Hurling,
        'muay-thai': SportType.MuayThai,
        'aussie-rules-football': SportType.AussieRulesFootball,
        'bandy': SportType.Bandy,
        'winter-olympics': SportType.WinterOlympic,
        'beach-volleyball': SportType.BeachVolleyball,
    };
    export function fromRouteName(routeName: string): SportType {
        return sportTypeRouteMapping[routeName.toLowerCase()] ?? SportType.Soccer;
    }

    export function toRouteName(sportType: SportType): string {
        const routeName = (Object.keys(sportTypeRouteMapping))
            .find(key => sportTypeRouteMapping[key] === sportType);
        if (routeName === undefined) {
            console.error(`transform sport type to route name error: ${sportType}`);
            return 'football';
        }
        return routeName;
    }

    const sportTypeRogueMapping: Record<SportType, RogueTiEnumSportType> = {
        [SportType.Soccer]: 1,
        [SportType.Basketball]: 2,
        [SportType.Football]: 3,
        [SportType.IceHockey]: 4,
        [SportType.Badminton]: 5,
        [SportType.Pool]: 6,
        [SportType.MotorSport]: 7,
        [SportType.Tennis]: 8,
        [SportType.Baseball]: 9,
        [SportType.Volleyball]: 10,
        [SportType.Others]: 11,
        [SportType.Golf]: 12,
        [SportType.Boxing]: 13,
        [SportType.Cricket]: 14,
        [SportType.TableTennis]: 15,
        [SportType.Rugby]: 16,
        [SportType.Handball]: 17,
        [SportType.Cycling]: 18,
        [SportType.Athletics]: 19,
        [SportType.BeachSoccer]: 20,
        [SportType.Futsal]: 21,
        [SportType.Entertainment]: 22,
        [SportType.Financial]: 23,
        [SportType.Darts]: 24,
        [SportType.Olympic]: 25,
        [SportType.Lacrosse]: 26,
        [SportType.WaterPolo]: 27,
        [SportType.WinterSports]: 28,
        [SportType.Squash]: 29,
        [SportType.GeneralHockey]: 30,
        [SportType.MixedMartialArts]: 31,
        [SportType.ESports]: 32,
        [SportType.GaelicFootball]: 33,
        [SportType.Hurling]: 34,
        [SportType.MuayThai]: 35,
        [SportType.AussieRulesFootball]: 36,
        [SportType.Bandy]: 37,
        [SportType.WinterOlympic]: 38,
        [SportType.BeachVolleyball]: 39,
        // does not exist in rogue
        [SportType.Unknown]: 0,
        [SportType.VirtualFootball]: 0,
        [SportType.VirtualBasketball]: 0,
        [SportType.VirtualRacing]: 0,
        [SportType.ESportsIm]: 0,
        [SportType.ESportsForDisplay]: 0,
        [SportType.Exchange]: 0,
        [SportType.Racing]: 0,
        [SportType.FantasySelection]: 0,
        [SportType.MuayThaiForDisplay]: 0,
        [SportType.VirtualNBA]: 0,
        [SportType.MuayThaiOneBx]: 0,
    };

    export function toRogue(sportType: SportType): RogueTiEnumSportType {
        return sportTypeRogueMapping[sportType];
    }
}

export enum MarketType {
    Unknown = 0,
    Handicap = 1,
    OddEven = 2,
    OverUnder = 3,
    CorrectScore = 4,
    _1X2 = 5,
    TotalGoal = 6,
    FH_Handicap = 7,
    FH_1X2 = 8,
    FH_OverUnder = 9,
    HTFT = 10,
    MoneyLine = 11,
    FH_OddEven = 12,
    FGLG = 13,
    FH_CorrectScore = 14,
    DoubleChance = 15,
    LiveScore = 16,
    FH_LiveScore = 17,
    Dynamic = 18,
    SH_CorrectScore = 19,
    Game = 38,
    OutRight = 39,
    MixParlay = 40,
    BetBuilder = 41,
}

export namespace MarketType {
    const marketTypeOddsApiMapping: Record<EnumMarketType, MarketType> = {
        [EnumMarketType.Unknown]: MarketType.Unknown,
        [EnumMarketType.Handicap]: MarketType.Handicap,
        [EnumMarketType.OddEven]: MarketType.OddEven,
        [EnumMarketType.OverUnder]: MarketType.OverUnder,
        [EnumMarketType.CorrectScore]: MarketType.CorrectScore,
        [EnumMarketType._1X2]: MarketType._1X2,
        [EnumMarketType.TotalGoal]: MarketType.TotalGoal,
        [EnumMarketType.FH_Handicap]: MarketType.FH_Handicap,
        [EnumMarketType.FH_1X2]: MarketType.FH_1X2,
        [EnumMarketType.FH_OverUnder]: MarketType.FH_OverUnder,
        [EnumMarketType.HTFT]: MarketType.HTFT,
        [EnumMarketType.MoneyLine]: MarketType.MoneyLine,
        [EnumMarketType.FH_OddEven]: MarketType.FH_OddEven,
        [EnumMarketType.FGLG]: MarketType.FGLG,
        [EnumMarketType.FH_CorrectScore]: MarketType.FH_CorrectScore,
        [EnumMarketType.DoubleChance]: MarketType.DoubleChance,
        [EnumMarketType.LiveScore]: MarketType.LiveScore,
        [EnumMarketType.FH_LiveScore]: MarketType.FH_LiveScore,
        [EnumMarketType.SH_CorrectScore]: MarketType.SH_CorrectScore,
        [EnumMarketType.Game]: MarketType.Game,
        [EnumMarketType.OutRight]: MarketType.OutRight,
        [EnumMarketType.MixParlay]: MarketType.MixParlay,
    };
    export function fromOddsApi(marketType: EnumMarketType): MarketType {
        return marketTypeOddsApiMapping[marketType];
    }

    export function toOddsApi(marketType: MarketType): EnumMarketType {
        const oddsApiMarketType = (Object.keys(marketTypeOddsApiMapping) as Array<EnumMarketType>)
            .find(key => marketTypeOddsApiMapping[key] === marketType);
        if (oddsApiMarketType === undefined) {
            console.error(`transform market type to odds api error: ${marketType}`);
            return EnumMarketType.Unknown;
        }
        return oddsApiMarketType;
    }

    export function fromPollux(marketType: TiEnumMarketType): MarketType {
        const marketTypePolluxMapping: Record<TiEnumMarketType, MarketType> = {
            [TiEnumMarketType.Unknown]: MarketType.Unknown,
            [TiEnumMarketType.Handicap]: MarketType.Handicap,
            [TiEnumMarketType.OddEven]: MarketType.OddEven,
            [TiEnumMarketType.OverUnder]: MarketType.OverUnder,
            [TiEnumMarketType.CorrectScore]: MarketType.CorrectScore,
            [TiEnumMarketType._1X2]: MarketType._1X2,
            [TiEnumMarketType.TotalGoal]: MarketType.TotalGoal,
            [TiEnumMarketType.FH_Handicap]: MarketType.FH_Handicap,
            [TiEnumMarketType.FH_1X2]: MarketType.FH_1X2,
            [TiEnumMarketType.FH_OverUnder]: MarketType.FH_OverUnder,
            [TiEnumMarketType.HTFT]: MarketType.HTFT,
            [TiEnumMarketType.MoneyLine]: MarketType.MoneyLine,
            [TiEnumMarketType.FH_OddEven]: MarketType.FH_OddEven,
            [TiEnumMarketType.FGLG]: MarketType.FGLG,
            [TiEnumMarketType.FH_CorrectScore]: MarketType.FH_CorrectScore,
            [TiEnumMarketType.DoubleChance]: MarketType.DoubleChance,
            [TiEnumMarketType.LiveScore]: MarketType.LiveScore,
            [TiEnumMarketType.FH_LiveScore]: MarketType.FH_LiveScore,
            [TiEnumMarketType.SH_CorrectScore]: MarketType.SH_CorrectScore,
            [TiEnumMarketType.Game]: MarketType.Game,
            [TiEnumMarketType.OutRight]: MarketType.OutRight,
            [TiEnumMarketType.MixParlay]: MarketType.MixParlay,
            [TiEnumMarketType.BetBuilder]: MarketType.BetBuilder,
            [TiEnumMarketType.Dynamic]: MarketType.Dynamic,
        };
        return marketTypePolluxMapping[marketType] ?? MarketType.Unknown;
    }

    export function toPollux(marketType: MarketType): TiEnumMarketType {
        const marketTypeToPolluxMapping: Record<MarketType, TiEnumMarketType> = {
            [MarketType.Unknown]: TiEnumMarketType.Unknown,
            [MarketType.Handicap]: TiEnumMarketType.Handicap,
            [MarketType.OddEven]: TiEnumMarketType.OddEven,
            [MarketType.OverUnder]: TiEnumMarketType.OverUnder,
            [MarketType.CorrectScore]: TiEnumMarketType.CorrectScore,
            [MarketType._1X2]: TiEnumMarketType._1X2,
            [MarketType.TotalGoal]: TiEnumMarketType.TotalGoal,
            [MarketType.FH_Handicap]: TiEnumMarketType.FH_Handicap,
            [MarketType.FH_1X2]: TiEnumMarketType.FH_1X2,
            [MarketType.FH_OverUnder]: TiEnumMarketType.FH_OverUnder,
            [MarketType.HTFT]: TiEnumMarketType.HTFT,
            [MarketType.MoneyLine]: TiEnumMarketType.MoneyLine,
            [MarketType.FH_OddEven]: TiEnumMarketType.FH_OddEven,
            [MarketType.FGLG]: TiEnumMarketType.FGLG,
            [MarketType.FH_CorrectScore]: TiEnumMarketType.FH_CorrectScore,
            [MarketType.DoubleChance]: TiEnumMarketType.DoubleChance,
            [MarketType.LiveScore]: TiEnumMarketType.LiveScore,
            [MarketType.FH_LiveScore]: TiEnumMarketType.FH_LiveScore,
            [MarketType.SH_CorrectScore]: TiEnumMarketType.SH_CorrectScore,
            [MarketType.Game]: TiEnumMarketType.Game,
            [MarketType.OutRight]: TiEnumMarketType.OutRight,
            [MarketType.MixParlay]: TiEnumMarketType.MixParlay,
            [MarketType.BetBuilder]: TiEnumMarketType.BetBuilder,
            [MarketType.Dynamic]: TiEnumMarketType.Dynamic,
        };
        return marketTypeToPolluxMapping[marketType] ?? TiEnumMarketType.Unknown;
    }
}

export enum MarketPage {
    Unknown = 'Unknown',
    Today = 'Today',
    EarlyMarket = 'EarlyMarket',
    Live = 'Live',
    All = 'All',
    MixParlay = 'MixParlay',
    Outright = 'Outright',
    EarlyMarketPlus1 = 'EarlyMarketPlus1',
    EarlyMarketPlus2 = 'EarlyMarketPlus2',
    EarlyMarketPlus3 = 'EarlyMarketPlus3',
    EarlyMarketPlus4Over = 'EarlyMarketPlus4Over',
}

export enum MarketGroupStyle {
    MainMarket = 1,
    MoreMarket = 2,
    NewBetType = 3,
}

export enum OddsStatus {
    Unknown = 0,
    Running = 1,
    Suspend = 2,
    Internal = 3,
    Closed = 4,
}

export namespace OddsStatus {
    export function fromOddsApi(oddsApiOddsStatus: EnumOddsStatus): OddsStatus {
        const oddsStatusMapping: Record<EnumOddsStatus, OddsStatus> = {
            [EnumOddsStatus.Unknown]: OddsStatus.Unknown,
            [EnumOddsStatus.Running]: OddsStatus.Running,
            [EnumOddsStatus.Suspend]: OddsStatus.Suspend,
            [EnumOddsStatus.Internal]: OddsStatus.Internal,
            [EnumOddsStatus.Closed]: OddsStatus.Closed,
        };
        return oddsStatusMapping[oddsApiOddsStatus];
    }
}

export enum OddsApiOddsStatus{
    Unknown = 0,
    Running = 1,
    Suspend = 2,
    Internal = 3,
    Closed = 4,
}

export enum BetStatus {
    Unknown = 0,
    Running = 1,
    Waiting = 2,
    Won = 3,
    Lose = 4,
    Draw = 5,
    Rejected = 6,
    Void = 7,
    Refunded = 8,
    SpecialVoid = 9,
    DangerousMpVoid = 13,
    CashedOut = 16,
    CashOut = 32,
    PartialCashOut = 64,
    IOM = 256,
    Special = 512,
    DelaySpecialVoid = 521,
    Dangerous1 = 1024,
    Dangerous2 = 2048,
    Dangerous3 = 4096,
    Cash = 8192,
    Indirect = 16384,
    SystemVoid = 32768,
    EarlySettled = 65536,
    DelayVoid = 131072,
    TestMode = 262144,
    Resettled = 524288,
    ThirdParty = 1048576,
    FreeBet = 67108864,
    GhostTicket = 134217728,
    MuayStep = 268435456,
    PremiumCricket = 536870912,
    ZeroCommission = 1073741824,
}

export namespace BetStatus {
    export function fromPollux(betStatus: PolluxBetStatus): BetStatus {
        const betStatusMapping: Record<PolluxBetStatus, BetStatus> = {
            [PolluxBetStatus.Unknown]: BetStatus.Unknown,
            [PolluxBetStatus.Running]: BetStatus.Running,
            [PolluxBetStatus.Waiting]: BetStatus.Waiting,
            [PolluxBetStatus.Won]: BetStatus.Won,
            [PolluxBetStatus.Lose]: BetStatus.Lose,
            [PolluxBetStatus.Draw]: BetStatus.Draw,
            [PolluxBetStatus.Rejected]: BetStatus.Rejected,
            [PolluxBetStatus.Void]: BetStatus.Void,
            [PolluxBetStatus.Refunded]: BetStatus.Refunded,
            [PolluxBetStatus.Special_Void]: BetStatus.SpecialVoid,
            [PolluxBetStatus.Dangerous_Mp_Void]: BetStatus.DangerousMpVoid,
            [PolluxBetStatus.Cashed_Out]: BetStatus.CashedOut,
            [PolluxBetStatus.Cash_Out]: BetStatus.CashOut,
            [PolluxBetStatus.PartialCashOut]: BetStatus.PartialCashOut,
            [PolluxBetStatus.I_O_M]: BetStatus.IOM,
            [PolluxBetStatus.Special]: BetStatus.Special,
            [PolluxBetStatus.DelaySpecialVoid]: BetStatus.DelaySpecialVoid,
            [PolluxBetStatus.Dangerous_1]: BetStatus.Dangerous1,
            [PolluxBetStatus.Dangerous_2]: BetStatus.Dangerous2,
            [PolluxBetStatus.Dangerous_3]: BetStatus.Dangerous3,
            [PolluxBetStatus.Cash]: BetStatus.Cash,
            [PolluxBetStatus.Indirect]: BetStatus.Indirect,
            [PolluxBetStatus.System_Void]: BetStatus.SystemVoid,
            [PolluxBetStatus.Early_Settled]: BetStatus.EarlySettled,
            [PolluxBetStatus.Delay_Void]: BetStatus.DelayVoid,
            [PolluxBetStatus.TestMode]: BetStatus.TestMode,
            [PolluxBetStatus.Resettled]: BetStatus.Resettled,
            [PolluxBetStatus.ThirdParty]: BetStatus.ThirdParty,
            [PolluxBetStatus.FreeBet]: BetStatus.FreeBet,
            [PolluxBetStatus.GhostTicket]: BetStatus.GhostTicket,
            [PolluxBetStatus.MuayStep]: BetStatus.MuayStep,
            [PolluxBetStatus.PremiumCricket]: BetStatus.PremiumCricket,
            [PolluxBetStatus.ZeroCommission]: BetStatus.ZeroCommission,
        };
        if (betStatusMapping[betStatus] === undefined) {
            console.error(`transform bet status from pollux error: ${betStatus}`);
            return BetStatus.Unknown;
        }
        return betStatusMapping[betStatus];
    }
}

export enum MainBetStatus {
    Unknown = 0,
    Amended = 1,
    Reversed = 2,
    Draw = 3,
    Lose = 4,
    Refunded = 5,
    WaitingRejected = 6,
    Running = 7,
    SpecialVoid = 8,
    Void = 9,
    Waiting = 10,
    Won = 11,
    CashOut = 12,
}

export namespace MainBetStatus {
    export function fromPollux(mainBetStatus: PolluxMainBetStatus): MainBetStatus {
        const betStatusMapping: Record<PolluxMainBetStatus, MainBetStatus> = {
            [PolluxMainBetStatus.Amended]: MainBetStatus.Amended,
            [PolluxMainBetStatus.Reversed]: MainBetStatus.Reversed,
            [PolluxMainBetStatus.Draw]: MainBetStatus.Draw,
            [PolluxMainBetStatus.Lose]: MainBetStatus.Lose,
            [PolluxMainBetStatus.Refunded]: MainBetStatus.Refunded,
            [PolluxMainBetStatus.WaitingRejected]: MainBetStatus.WaitingRejected,
            [PolluxMainBetStatus.Running]: MainBetStatus.Running,
            [PolluxMainBetStatus.SpecialVoid]: MainBetStatus.SpecialVoid,
            [PolluxMainBetStatus.Void]: MainBetStatus.Void,
            [PolluxMainBetStatus.Waiting]: MainBetStatus.Waiting,
            [PolluxMainBetStatus.Won]: MainBetStatus.Won,
            [PolluxMainBetStatus.CashOut]: MainBetStatus.CashOut,
        };
        if (betStatusMapping[mainBetStatus] === undefined) {
            console.error(`transform main bet status from pollux error: ${mainBetStatus}`);
            return MainBetStatus.Unknown;
        }
        return betStatusMapping[mainBetStatus];
    }
}

export enum SubBetStatus {
    Unknown = 0,
    Draw = 1,
    Lose = 2,
    Running = 3,
    Void = 4,
    Waiting = 5,
    Won = 6,
    HalfWon = 7,
    HalfLose = 8,
    Suspended = 9,
    WaitingRejected = 10,
    Done = 11,
    Refund = 12,
}

export namespace SubBetStatus {
    export function fromPollux(subBetStatus: PolluxSubBetStatus): SubBetStatus {
        const statusMapping: Record<PolluxSubBetStatus, SubBetStatus> = {
            [PolluxSubBetStatus.Draw]: SubBetStatus.Draw,
            [PolluxSubBetStatus.Lose]: SubBetStatus.Lose,
            [PolluxSubBetStatus.Running]: SubBetStatus.Running,
            [PolluxSubBetStatus.Void]: SubBetStatus.Void,
            [PolluxSubBetStatus.Waiting]: SubBetStatus.Waiting,
            [PolluxSubBetStatus.Won]: SubBetStatus.Won,
            [PolluxSubBetStatus.HalfWon]: SubBetStatus.HalfWon,
            [PolluxSubBetStatus.HalfLose]: SubBetStatus.HalfLose,
            [PolluxSubBetStatus.Suspended]: SubBetStatus.Suspended,
            [PolluxSubBetStatus.WaitingRejected]: SubBetStatus.WaitingRejected,
            [PolluxSubBetStatus.Done]: SubBetStatus.Done,
            [PolluxSubBetStatus.Refund]: SubBetStatus.Refund,
        };

        if (statusMapping[subBetStatus] === undefined) {
            console.error(`transform main bet status from pollux error: ${subBetStatus}`);
            return SubBetStatus.Unknown;
        }
        return statusMapping[subBetStatus];
    }
}

export enum ESportsBetStatus {
    Unknown = 0,
    Running = 1,
    Waiting = 2,
    Won = 3,
    Lose = 4,
    Draw = 5,
    Rejected = 6,
    Void = 7,
    Refunded = 8,
    SpecialVoid = 9,
    HalfWon = 10,
    HalfLose = 11,
    SuspendVoid = 12,
    DangerousMpVoid = 13,
}

export namespace ESportsBetStatus {
    export function fromPollux(betStatus: number): ESportsBetStatus {
        return betStatus as unknown as ESportsBetStatus;
    }
}

export enum PriceStyle {
    Unknown = 0,
    Malay = 1,
    HK = 2,
    Indo = 3,
    Euro = 4
}

export namespace PriceStyle {
    export function fromPollux(priceStyle: TiEnumPriceStyle | DisplayOddStyle): PriceStyle {
        switch (priceStyle) {
            case TiEnumPriceStyle.Euro:
            case DisplayOddStyle.Euro:
                return PriceStyle.Euro;
            case TiEnumPriceStyle.HK:
            case DisplayOddStyle.HK:
                return PriceStyle.HK;
            case TiEnumPriceStyle.Indo:
            case DisplayOddStyle.Indo:
                return PriceStyle.Indo;
            case TiEnumPriceStyle.Malay:
            case DisplayOddStyle.Malay:
                return PriceStyle.Malay;
            case TiEnumPriceStyle.Unknown:
            case DisplayOddStyle.Unknown:
                return PriceStyle.Unknown;
            default: console.error(`transform price style from pollux error: ${priceStyle}`); return PriceStyle.Unknown;
        }
    }

    export function toPollux(priceStyle: PriceStyle): TiEnumPriceStyle {
        switch (priceStyle) {
            case PriceStyle.Euro:
                return TiEnumPriceStyle.Euro;
            case PriceStyle.HK:
                return TiEnumPriceStyle.HK;
            case PriceStyle.Indo:
                return TiEnumPriceStyle.Indo;
            case PriceStyle.Malay:
                return TiEnumPriceStyle.Malay;
            case PriceStyle.Unknown:
                return TiEnumPriceStyle.Unknown;
            default: console.error(`transform price style to pollux error: ${priceStyle}`); return TiEnumPriceStyle.Unknown;
        }
    }

    export function toOddsApi(priceStyle: PriceStyle): EnumPriceStyle {
        const oddsApiPriceStyleMapping: Record<PriceStyle, EnumPriceStyle> = {
            [PriceStyle.Unknown]: EnumPriceStyle.Malay,
            [PriceStyle.Malay]: EnumPriceStyle.Malay,
            [PriceStyle.HK]: EnumPriceStyle.HK,
            [PriceStyle.Indo]: EnumPriceStyle.Indo,
            [PriceStyle.Euro]: EnumPriceStyle.Euro,
        };
        if (oddsApiPriceStyleMapping[priceStyle] === undefined) {
            console.error(`transform price style to odds api error: ${priceStyle}`);
            return EnumPriceStyle.Malay;
        }
        return oddsApiPriceStyleMapping[priceStyle];
    }

    export function fromOddsApi(oddsStyle: EnumOddsStyle): PriceStyle {
        switch (oddsStyle) {
            case EnumOddsStyle.Euro:
                return PriceStyle.Euro;
            case EnumOddsStyle.Hk:
                return PriceStyle.HK;
            case EnumOddsStyle.Indo:
                return PriceStyle.Indo;
            case EnumOddsStyle.Malay:
                return PriceStyle.Malay;
            default:
                return PriceStyle.Unknown;
        }
    }
}

export enum OpenTicketStatus {
    Success = 0,
    InvalidInput = 1,
    EventCompleted = 2,
    EventClosed = 3,
    EventInternal = 4,
    OddsClosed = 5,
    OddsInternal = 6,
    OddsCompleted = 7,
    MaxOddsExceed = 8,
    InvalidPrice = 9,
}

export namespace OpenTicketStatus {
    export function fromPollux(status: PolluxOpenTicketStatus): OpenTicketStatus {
        const mapping: Record<PolluxOpenTicketStatus, OpenTicketStatus> = {
            [PolluxOpenTicketStatus.Success]: OpenTicketStatus.Success,
            [PolluxOpenTicketStatus.InvalidInput]: OpenTicketStatus.InvalidInput,
            [PolluxOpenTicketStatus.EventCompleted]: OpenTicketStatus.EventCompleted,
            [PolluxOpenTicketStatus.EventClosed]: OpenTicketStatus.EventClosed,
            [PolluxOpenTicketStatus.EventInternal]: OpenTicketStatus.EventInternal,
            [PolluxOpenTicketStatus.OddsClosed]: OpenTicketStatus.OddsClosed,
            [PolluxOpenTicketStatus.OddsInternal]: OpenTicketStatus.OddsInternal,
            [PolluxOpenTicketStatus.OddsCompleted]: OpenTicketStatus.OddsCompleted,
            [PolluxOpenTicketStatus.MaxOddsExceed]: OpenTicketStatus.MaxOddsExceed,
            [PolluxOpenTicketStatus.InvalidPrice]: OpenTicketStatus.InvalidPrice,
        };

        return mapping[status];
    }
}

export enum BetTypeGroup {
    Unknown = -1,
    Main = 0,
    Soccer_TotalBookings = 111,
    Soccer_HomeTeamTotalGoal = 123,
    Soccer_AwayTeamTotalGoal = 124,
    Soccer_TotalCorner = 126,
    Soccer_HighestScoringHalf = 158,
    Soccer_FirstFifteenMinuteFastMarket = 266,
    Soccer_LastFifteenMinuteFastMarket = 269,
    Soccer_FirstGoal = 306,
    Soccer_SecondGoal = 307,
    Soccer_ThirdGoal = 308,
    Soccer_ForthGoal = 309,
    Soccer_FifthGoal = 310,
    Soccer_SixthGoal = 311,
    Soccer_SeventhGoal = 312,
    Soccer_EighthGoal = 313,
    Soccer_NinthGoal = 330,
    Soccer_TenthGoal = 331,
    Soccer_FirstOneMinuteFastMarket = 638,
    Soccer_LastOneMinuteFastMarket = 727,
    Soccer_FirstFiveMinuteFastMarket = 728,
    Soccer_LastFiveMinuteFastMarket = 745,
    Soccer_YellowCard = 321076,
    Soccer_PenaltyTenKick = 1066212,
    Basketball_FirstQuarter = 129,
    Basketball_FirstBasket = 130,
    Basketball_LastBasket = 131,
    Basketball_RaceToFirst20Point = 134,
    Basketball_TeamWithHighestScoringQuarter = 173,
    Basketball_FirstHalfMoneyLine = 624,
    Tennis_GameHandicap = 140,
    Tennis_FirstSet = 357,
    Tennis_SecondSet = 358,
    AmericanFootball_FirstQuarter = 136,
    IceHockey_FirstPeriod = 153,
    IceHockey_SecondPeriod = 154,
    Baseball_HomeTeamTotalRuns = 250,
    Baseball_AwayTeamTotalRuns = 251,
    Baseball_FirstInningRuns = 337,
    Futsal_HomeTeamTotalGoal = 620859,
    Futsal_AwayTeamTotalGoal = 620860,
    Futsal_FirstHalf = 626336,
    Badminton_TotalMatchPoints = 179,
    Badminton_FirstSet = 376,
    Badminton_SecondSet = 377,
    TableTennis_TotalMatchPoints = 281846,
    TableTennis_FirstSetPointHandicap = 281847,
    TableTennis_ThirdSetPointHandicap = 281849,
    Volleyball_TotalMatchPoints = 174,
    Volleyball_FirstSetBetting = 181,
    Volleyball_ThirdSetBetting = 456,
}

export enum MatchFunction {
    Unknown = 0,
    LiveCourt = 1,
    LiveStream = 2,
    Statistics = 3,
}

export enum CashOutState {
    Countdown = 'countdown',
    Confirm = 'confirm',
    Cashing = 'cashing',
    Cashed = 'cashed',
    Unavailable = 'unavailable',
}

export enum DisplayMode {
    ShowAll = 'SHOW_ALL',
    ScrollDown = 'SCROLL_DOWN',
    ShowInfo = 'SHOW_INFO',
}

export enum BetSlipType {
    None = 'None',
    Single = 'Single',
    SingleMode = 'SingleMode',
    Outright = 'Outright',
    Parlay = 'Parlay',
    BetBuilder = 'BetBuilder',
    Dynamic = 'Dynamic',
}

export enum TicketType {
    Single = 'single',
    DynamicMarket = 'dynamicMarket',
    Outright = 'outright',
}

export enum SingleModeBetSlipTab {
    Single = 'single',
    Parlay = 'parlay',
}

export enum SingleModeTicketLayout {
    Single = 'single',
    Parlay = 'parlay',
}

export enum BetSlipCtaAction {
    PlaceBet = 'placeBet',
    UpdateStake = 'updateStake',
    AcceptChange = 'acceptChange',
    Deposit = 'deposit',
}

export enum SingleModeBalanceError {
    BelowMinimumStake = 'belowMinimumStake',
    SingleStakeExceedsBalance = 'singleStakeExceedsBalance',
    MultipleStakesExceedBalance = 'multipleStakesExceedBalance',
}

export enum ParlayStakeError {
    BelowMinimum = 'belowMinimum',
    AboveMaximum = 'aboveMaximum',
}

export enum BetSlipState {
    Closed = 'Closed',
    Single = 'Single',
    BetBuilder = 'BetBuilder',
    Mini = 'Mini',
    Medium = 'Medium',
    Big = 'Big',
}

export enum SwitchState {
    None,
    Match,
    Market,
    Option
}

export enum LiveScoreProvider {
    None = -1,
    Sbo = 0,
    Sdt = 1,
    BetRadar = 2,
    Cricket = 3,
    BetConstruct = 4,
    BetGenius = 5,
}

export enum MatchStatus {
    Unknown = -1,
    NotStarted = 0,
    Running = 1,
    Break = 2,
    Pause = 3,
    Ended = 4,
}

export namespace MatchStatus {
    export function fromOddsApi(matchStatus: number): MatchStatus {
        const isValidStatus = Object.values(MatchStatus).includes(matchStatus);
        if (!isValidStatus) {
            console.error(`transform match status from odds api error: ${matchStatus}`);
            return MatchStatus.Unknown;
        }
        return matchStatus;
    }
}

export enum SirenSSOLocation {
    PromotionsPage = 1,
    SpecificPromotionPage = 2,
    ReferAFriend = 3,
}

export enum PromotionBannerType {
    MixParlayPromotion = 3,
    VirtualSportsVoucher = 6,
    ResponsibleGambling = 9,
    ChristmasPromotion = 11,
    LiveCasinoPromotion = 13,
    LinePromotion = 14,
    LiveCasinoMobilePromotion = 15,
    YearEndReview = 18,
    GamePromotion = 19,
    SurveyInvitation = 20,
    BvBLuckyDrawPromotion = 26,
    SboClubSportsChallenge = 27,
    InJoiPGPopupBanner = 28,
    QuestionnaireBanner = 29,
    SearchFunctionOnboardingScreen = 500,
    AfricaCupPromotion = 503,
    NbaPromotion = 504,
    SportsPromotion = 505,
    SportsCupPromotion = 507,
    ESportsBanner = 600,
    FifaSportsBanner = 601,
    IplLuckyDip = 602,
    DailyMission = 603,
    PlatformPromotion =701
}

export enum RoutePage {
    MyBets = 'MyBets',
    CashOut = 'CashOut',
    Results = 'Results',
    MyMatches = 'MyMatches',
    Exchange = 'Exchange',
    ExchangeTransfer = 'ExchangeTransfer',
    MatchDetail = 'MatchDetail',
    SportsCupPage = 'SportsCupPage',
    EFootballPage = 'EFootballPage',
    MultipleSportsZone = 'MultipleSportsZone',
    SimulationSportsZone = 'SimulationSportsZone',
    MainBySport = 'MainBySport',
    Main = 'Main',
    NotFound = 'NotFound',
    Jackpot = 'Jackpot',
    TournamentCenterOverview = 'TournamentCenterOverview',
    TournamentCenterCountries = 'TournamentCenterCountries',
    TournamentCenterMatches = 'TournamentCenterMatches',
    TournamentCenterPromotion = 'TournamentCenterPromotion',
    FantasySelectionHome = 'FantasySelectionHome', // tera product switch use this value
    FantasySelectionContestPools = 'FantasySelectionContestPools',
    FantasySelectionContestDetail = 'FantasySelectionContestDetail',
    FantasySelectionMyBets = 'FantasySelectionMyBets',
    FantasySelectionMyBetDetail = 'FantasySelectionMyBetDetail',
}

export enum ProductName {
    Racing = 'Racing',
    Sports = 'Sports',
    VirtualSports = 'VirtualSports',
    eSports = 'eSports',
}

export enum ShowTimeDisplayType {
    None = 0,
    Date = 1,
    DateTime = 2,
    RedLive = 4,
    BlueLive = 8,
}

export enum MessageLevel{
    Success = 'Success',
    Error ='Error',
}

export enum ExchangeTransferAction{
    TransferIn = 'TransferIn',
    TransferOut = 'TransferOut',
}

export enum ExchangeStatement{
    ExchangeBets = 'ExchangeBets',
    TransferHistory = 'TransferHistory',
}

export enum ExchangeTransferStatus{
    Unknown = 0,
    Success = 1,
    Error = 2,
    ThirdPartyError = 3,
}

export namespace ExchangeTransferStatus {
    export function fromPollux(status: PolluxExchangeStatus): ExchangeTransferStatus {
        const mapping: Record<PolluxExchangeStatus, ExchangeTransferStatus> = {
            [PolluxExchangeStatus.Unknown]: ExchangeTransferStatus.Unknown,
            [PolluxExchangeStatus.Success]: ExchangeTransferStatus.Success,
            [PolluxExchangeStatus.Error]: ExchangeTransferStatus.Error,
            [PolluxExchangeStatus.ThirdPartyError]: ExchangeTransferStatus.ThirdPartyError,
        };

        return mapping[status] ?? ExchangeTransferStatus.Unknown;
    }
}

export enum TransferAmountError {
    None,
    MoreThanBalance,
    MoreThanExchangeBalance,
    MoreThanBettingBudget,
    InvalidFormat,
    Required,
    AccountProblem,
}

export enum ZIndex {
    HeaderSportSwitch,
    HeaderMarketSwitch,
    HeaderCollapse,
    GenericHeader,
    Widget,
    BottomFunction,
    BetBuilderPopup,
    AcceptAnyOddsPopup,
    VoucherRedeemPopup,
    PartialCashOutPopup,
    AcceptAnyCashOutValuePopup,
    TelegramBotPopup,
    Tutorial,
}

export enum BannerPosition {
    CastorTop = 0,
    MyBetsTop = 1,
}

export namespace BannerPosition {
    export function toPollux(bannerPosition: BannerPosition): PolluxBannerPosition {
        const bannerPositionMapping: Record<BannerPosition, PolluxBannerPosition> = {
            [BannerPosition.CastorTop]: PolluxBannerPosition.CastorTop,
            [BannerPosition.MyBetsTop]: PolluxBannerPosition.CastorMyBetTop,
        };
        if (bannerPositionMapping[bannerPosition] === undefined) {
            console.error(`transform banner position to pollux error: ${bannerPosition}`);
            return PolluxBannerPosition.CastorTop;
        }
        return bannerPositionMapping[bannerPosition];
    }
}

export enum BannerAction {
    Unknown,
    NoAction,
    ProductSwitch,
    JoinNow,
    ToSport,
    ToSportMarketPage,
    ToSportLeague,
    SimulateClick,
    RouteTo,
    RedirectTo,
    OpenWindow,
    InJoiOpenGame,
    ToPromotionZone,
    InJoiEventPromotion,
    CasinoInGame,
    CasinoToGameLobby,
    CasinoDealerPage
}

export namespace BannerAction {
    export function fromPollux(action: EnumSportsBannerAction): BannerAction {
        const bannerActionMapping: Record<EnumSportsBannerAction, BannerAction> = {
            [EnumSportsBannerAction.Unknown]: BannerAction.Unknown,
            [EnumSportsBannerAction.NoAction]: BannerAction.NoAction,
            [EnumSportsBannerAction.ProductSwitch]: BannerAction.ProductSwitch,
            [EnumSportsBannerAction.JoinNow]: BannerAction.JoinNow,
            [EnumSportsBannerAction.ToSport]: BannerAction.ToSport,
            [EnumSportsBannerAction.ToSportMarketPage]: BannerAction.ToSportMarketPage,
            [EnumSportsBannerAction.ToSportLeague]: BannerAction.ToSportLeague,
            [EnumSportsBannerAction.SimulateClick]: BannerAction.SimulateClick,
            [EnumSportsBannerAction.RouteTo]: BannerAction.RouteTo,
            [EnumSportsBannerAction.RedirectTo]: BannerAction.RedirectTo,
            [EnumSportsBannerAction.OpenWindow]: BannerAction.OpenWindow,
            [EnumSportsBannerAction.InJoiOpenGame]: BannerAction.InJoiOpenGame,
            [EnumSportsBannerAction.ToPromotionZone]: BannerAction.ToPromotionZone,
            [EnumSportsBannerAction.InJoiEventPromotion]: BannerAction.InJoiEventPromotion,
            [EnumSportsBannerAction.CasinoInGame]: BannerAction.CasinoInGame,
            [EnumSportsBannerAction.CasinoToGameLobby]: BannerAction.CasinoToGameLobby,
            [EnumSportsBannerAction.CasinoDealerPage]: BannerAction.CasinoDealerPage,
        };
        if (bannerActionMapping[action] === undefined) {
            console.error(`transform banner action from pollux error: ${action}`);
            return BannerAction.Unknown;
        }
        return bannerActionMapping[action];
    }
}

export enum BrandType {
    Unknown,
    Sbotop,
    Sbobet,
}

export namespace BrandType {
    export function fromPollux(brandType: PolluxBrandType): BrandType {
        const brandTypeMapping: Record<PolluxBrandType, BrandType> = {
            [PolluxBrandType.Unknown]: BrandType.Unknown,
            [PolluxBrandType.SBOBET]: BrandType.Sbobet,
            [PolluxBrandType.SBOTOP]: BrandType.Sbotop,
        };
        if (brandTypeMapping[brandType] === undefined) {
            console.error(`transform brand type from pollux error: ${brandType}`);
            return BrandType.Unknown;
        }
        return brandTypeMapping[brandType];
    }

    export function toPollux(brandType: BrandType): PolluxBrandType {
        const brandTypeMapping: Partial<Record<BrandType, PolluxBrandType>> = {
            [BrandType.Sbobet]: PolluxBrandType.SBOBET,
            [BrandType.Sbotop]: PolluxBrandType.SBOTOP,
        };
        const brand = brandTypeMapping[brandType];
        if (brand === undefined) {
            throw new Error(`transform brand type to pollux error: ${brandType}`);
        }
        return brand;
    }
}

export enum LicenseType {
    MNL,
    IOM,
}

export namespace LicenseType {
    export function fromPollux(license: PolluxLicenseType): LicenseType {
        const licenseTypeMapping: Record<PolluxLicenseType, LicenseType> = {
            [PolluxLicenseType.MNL]: LicenseType.MNL,
            [PolluxLicenseType.IOM]: LicenseType.IOM,
        };
        if (licenseTypeMapping[license] === undefined) {
            throw new Error(`transform license type from pollux error: ${license}`);
        }
        return licenseTypeMapping[license];
    }
}

export enum PlayerType {
    B2B,
    B2C,
    B2B2C,
}

export namespace PlayerType {
    export function fromPollux(playerType: PolluxPlayerType): PlayerType {
        const playerTypeMapping: Record<PolluxPlayerType, PlayerType> = {
            [PolluxPlayerType.B2B]: PlayerType.B2B,
            [PolluxPlayerType.B2C]: PlayerType.B2C,
            [PolluxPlayerType.B2B2C]: PlayerType.B2B2C,
        };
        if (playerTypeMapping[playerType] === undefined) {
            throw new Error(`transform player type from pollux error: ${playerType}`);
        }
        return playerTypeMapping[playerType];
    }
}

export enum CashOutStatus {
    Unknown,
    Success,
    CallJackTimeNotEnabled,
    InvalidTransactionId,
    InvalidBets,
    ExternalProviderFailure,
    ArgoFailure,
    CashOutValueTooHigh,
    SettlementInProgress,
    InternalError,
    GeneralError,
}

export namespace CashOutStatus {
    export function fromPollux(status: PolluxCashOutStatus): CashOutStatus {
        const cashOutStatusMapping: Partial<Record<PolluxCashOutStatus, CashOutStatus>> = {
            [PolluxCashOutStatus.Success]: CashOutStatus.Success,
            [PolluxCashOutStatus.CallJackTimeNotEnabled]: CashOutStatus.CallJackTimeNotEnabled,
            [PolluxCashOutStatus.InvalidTransactionId]: CashOutStatus.InvalidTransactionId,
            [PolluxCashOutStatus.InvalidBets]: CashOutStatus.InvalidBets,
            [PolluxCashOutStatus.ExternalProviderFailure]: CashOutStatus.ExternalProviderFailure,
            [PolluxCashOutStatus.ArgoFailure]: CashOutStatus.ArgoFailure,
            [PolluxCashOutStatus.CashOutValueTooHigh]: CashOutStatus.CashOutValueTooHigh,
            [PolluxCashOutStatus.SettlementInProgress]: CashOutStatus.SettlementInProgress,
            [PolluxCashOutStatus.InternalError]: CashOutStatus.InternalError,
            [PolluxCashOutStatus.RateLimited]: CashOutStatus.GeneralError,
        };
        const cashOutStatus = cashOutStatusMapping[status];
        if (cashOutStatus === undefined) {
            console.error(`transform cash out status from pollux error: ${status}`);
            return CashOutStatus.Unknown;
        }
        return cashOutStatus;
    }
}

export enum OddsOption {
    Unknown,
    Home,
    Away,
    Over,
    Under,
    Odd,
    Even,
    _1X2_1,
    _1X2_X,
    _1X2_2,
    ML_1,
    ML_2,
    HTFT_HH,
    HTFT_HA,
    HTFT_HD,
    HTFT_AH,
    HTFT_AA,
    HTFT_AD,
    HTFT_DH,
    HTFT_DA,
    HTFT_DD,
    FGLG_HF,
    FGLG_HL,
    FGLG_AF,
    FGLG_AL,
    FGLG_NG,
    TG_0_1,
    TG_2_3,
    TG_4_6,
    TG_7_Up,
    CS_0_0,
    CS_0_1,
    CS_0_2,
    CS_0_3,
    CS_0_4,
    CS_1_0,
    CS_1_1,
    CS_1_2,
    CS_1_3,
    CS_1_4,
    CS_2_0,
    CS_2_1,
    CS_2_2,
    CS_2_3,
    CS_2_4,
    CS_3_0,
    CS_3_1,
    CS_3_2,
    CS_3_3,
    CS_3_4,
    CS_4_0,
    CS_4_1,
    CS_4_2,
    CS_4_3,
    CS_4_4,
    CS_HUP5,
    CS_AUP5,
    OR,
    DC_1X,
    DC_2X,
    DC_12,
    GAME_0_0,
    GAME_0_1,
    GAME_0_2,
    GAME_0_3,
    GAME_0_4,
    GAME_0_5,
    GAME_1_0,
    GAME_1_1,
    GAME_1_2,
    GAME_1_3,
    GAME_1_4,
    GAME_2_0,
    GAME_2_1,
    GAME_2_2,
    GAME_2_3,
    GAME_2_4,
    GAME_3_0,
    GAME_3_1,
    GAME_3_2,
    GAME_3_3,
    GAME_3_4,
    GAME_4_0,
    GAME_4_1,
    GAME_4_2,
    GAME_4_3,
    GAME_4_4,
    GAME_5_0,
    LS_1,
    LS_X,
    LS_2,
    CS_Others,
}

export namespace OddsOption {
    export function fromPollux(oddsOption: TiEnumOddsOption): OddsOption {
        const oddsOptionMapping: Record<TiEnumOddsOption, OddsOption> = {
            [TiEnumOddsOption.Unknown]: OddsOption.Unknown,
            [TiEnumOddsOption.Home]: OddsOption.Home,
            [TiEnumOddsOption.Away]: OddsOption.Away,
            [TiEnumOddsOption.Over]: OddsOption.Over,
            [TiEnumOddsOption.Under]: OddsOption.Under,
            [TiEnumOddsOption.Odd]: OddsOption.Odd,
            [TiEnumOddsOption.Even]: OddsOption.Even,
            [TiEnumOddsOption._1X2_1]: OddsOption._1X2_1,
            [TiEnumOddsOption._1X2_X]: OddsOption._1X2_X,
            [TiEnumOddsOption._1X2_2]: OddsOption._1X2_2,
            [TiEnumOddsOption.ML_1]: OddsOption.ML_1,
            [TiEnumOddsOption.ML_2]: OddsOption.ML_2,
            [TiEnumOddsOption.HTFT_HH]: OddsOption.HTFT_HH,
            [TiEnumOddsOption.HTFT_HA]: OddsOption.HTFT_HA,
            [TiEnumOddsOption.HTFT_HD]: OddsOption.HTFT_HD,
            [TiEnumOddsOption.HTFT_AH]: OddsOption.HTFT_AH,
            [TiEnumOddsOption.HTFT_AA]: OddsOption.HTFT_AA,
            [TiEnumOddsOption.HTFT_AD]: OddsOption.HTFT_AD,
            [TiEnumOddsOption.HTFT_DH]: OddsOption.HTFT_DH,
            [TiEnumOddsOption.HTFT_DA]: OddsOption.HTFT_DA,
            [TiEnumOddsOption.HTFT_DD]: OddsOption.HTFT_DD,
            [TiEnumOddsOption.FGLG_HF]: OddsOption.FGLG_HF,
            [TiEnumOddsOption.FGLG_HL]: OddsOption.FGLG_HL,
            [TiEnumOddsOption.FGLG_AF]: OddsOption.FGLG_AF,
            [TiEnumOddsOption.FGLG_AL]: OddsOption.FGLG_AL,
            [TiEnumOddsOption.FGLG_NG]: OddsOption.FGLG_NG,
            [TiEnumOddsOption.TG_0_1]: OddsOption.TG_0_1,
            [TiEnumOddsOption.TG_2_3]: OddsOption.TG_2_3,
            [TiEnumOddsOption.TG_4_6]: OddsOption.TG_4_6,
            [TiEnumOddsOption.TG_7_Up]: OddsOption.TG_7_Up,
            [TiEnumOddsOption.CS_0_0]: OddsOption.CS_0_0,
            [TiEnumOddsOption.CS_0_1]: OddsOption.CS_0_1,
            [TiEnumOddsOption.CS_0_2]: OddsOption.CS_0_2,
            [TiEnumOddsOption.CS_0_3]: OddsOption.CS_0_3,
            [TiEnumOddsOption.CS_0_4]: OddsOption.CS_0_4,
            [TiEnumOddsOption.CS_1_0]: OddsOption.CS_1_0,
            [TiEnumOddsOption.CS_1_1]: OddsOption.CS_1_1,
            [TiEnumOddsOption.CS_1_2]: OddsOption.CS_1_2,
            [TiEnumOddsOption.CS_1_3]: OddsOption.CS_1_3,
            [TiEnumOddsOption.CS_1_4]: OddsOption.CS_1_4,
            [TiEnumOddsOption.CS_2_0]: OddsOption.CS_2_0,
            [TiEnumOddsOption.CS_2_1]: OddsOption.CS_2_1,
            [TiEnumOddsOption.CS_2_2]: OddsOption.CS_2_2,
            [TiEnumOddsOption.CS_2_3]: OddsOption.CS_2_3,
            [TiEnumOddsOption.CS_2_4]: OddsOption.CS_2_4,
            [TiEnumOddsOption.CS_3_0]: OddsOption.CS_3_0,
            [TiEnumOddsOption.CS_3_1]: OddsOption.CS_3_1,
            [TiEnumOddsOption.CS_3_2]: OddsOption.CS_3_2,
            [TiEnumOddsOption.CS_3_3]: OddsOption.CS_3_3,
            [TiEnumOddsOption.CS_3_4]: OddsOption.CS_3_4,
            [TiEnumOddsOption.CS_4_0]: OddsOption.CS_4_0,
            [TiEnumOddsOption.CS_4_1]: OddsOption.CS_4_1,
            [TiEnumOddsOption.CS_4_2]: OddsOption.CS_4_2,
            [TiEnumOddsOption.CS_4_3]: OddsOption.CS_4_3,
            [TiEnumOddsOption.CS_4_4]: OddsOption.CS_4_4,
            [TiEnumOddsOption.CS_HUP5]: OddsOption.CS_HUP5,
            [TiEnumOddsOption.CS_AUP5]: OddsOption.CS_AUP5,
            [TiEnumOddsOption.OR]: OddsOption.OR,
            [TiEnumOddsOption.DC_1X]: OddsOption.DC_1X,
            [TiEnumOddsOption.DC_2X]: OddsOption.DC_2X,
            [TiEnumOddsOption.DC_12]: OddsOption.DC_12,
            [TiEnumOddsOption.GAME_0_0]: OddsOption.GAME_0_0,
            [TiEnumOddsOption.GAME_0_1]: OddsOption.GAME_0_1,
            [TiEnumOddsOption.GAME_0_2]: OddsOption.GAME_0_2,
            [TiEnumOddsOption.GAME_0_3]: OddsOption.GAME_0_3,
            [TiEnumOddsOption.GAME_0_4]: OddsOption.GAME_0_4,
            [TiEnumOddsOption.GAME_0_5]: OddsOption.GAME_0_5,
            [TiEnumOddsOption.GAME_1_0]: OddsOption.GAME_1_0,
            [TiEnumOddsOption.GAME_1_1]: OddsOption.GAME_1_1,
            [TiEnumOddsOption.GAME_1_2]: OddsOption.GAME_1_2,
            [TiEnumOddsOption.GAME_1_3]: OddsOption.GAME_1_3,
            [TiEnumOddsOption.GAME_1_4]: OddsOption.GAME_1_4,
            [TiEnumOddsOption.GAME_2_0]: OddsOption.GAME_2_0,
            [TiEnumOddsOption.GAME_2_1]: OddsOption.GAME_2_1,
            [TiEnumOddsOption.GAME_2_2]: OddsOption.GAME_2_2,
            [TiEnumOddsOption.GAME_2_3]: OddsOption.GAME_2_3,
            [TiEnumOddsOption.GAME_2_4]: OddsOption.GAME_2_4,
            [TiEnumOddsOption.GAME_3_0]: OddsOption.GAME_3_0,
            [TiEnumOddsOption.GAME_3_1]: OddsOption.GAME_3_1,
            [TiEnumOddsOption.GAME_3_2]: OddsOption.GAME_3_2,
            [TiEnumOddsOption.GAME_3_3]: OddsOption.GAME_3_3,
            [TiEnumOddsOption.GAME_3_4]: OddsOption.GAME_3_4,
            [TiEnumOddsOption.GAME_4_0]: OddsOption.GAME_4_0,
            [TiEnumOddsOption.GAME_4_1]: OddsOption.GAME_4_1,
            [TiEnumOddsOption.GAME_4_2]: OddsOption.GAME_4_2,
            [TiEnumOddsOption.GAME_4_3]: OddsOption.GAME_4_3,
            [TiEnumOddsOption.GAME_4_4]: OddsOption.GAME_4_4,
            [TiEnumOddsOption.GAME_5_0]: OddsOption.GAME_5_0,
            [TiEnumOddsOption.LS_1]: OddsOption.LS_1,
            [TiEnumOddsOption.LS_X]: OddsOption.LS_X,
            [TiEnumOddsOption.LS_2]: OddsOption.LS_2,
            [TiEnumOddsOption.CS_Others]: OddsOption.CS_Others,
        };
        if (oddsOptionMapping[oddsOption] === undefined) {
            console.error(`transform odds option from pollux error: ${oddsOption}`);
            return OddsOption.Unknown;
        }
        return oddsOptionMapping[oddsOption];
    }

    export function toString(oddsOption: OddsOption) {
        const oddsOptionStringMapping: Partial<Record<OddsOption, string>> = {
            [OddsOption.Unknown]: '',
            [OddsOption.Home]: 'h',
            [OddsOption.Away]: 'a',
            [OddsOption.Over]: 'h',
            [OddsOption.Under]: 'a',
            [OddsOption.Odd]: 'h',
            [OddsOption.Even]: 'a',
            [OddsOption._1X2_1]: '1',
            [OddsOption._1X2_X]: 'x',
            [OddsOption._1X2_2]: '2',
            [OddsOption.ML_1]: '1',
            [OddsOption.ML_2]: '2',
            [OddsOption.HTFT_HH]: 'hh',
            [OddsOption.HTFT_HA]: 'ha',
            [OddsOption.HTFT_HD]: 'hd',
            [OddsOption.HTFT_AH]: 'ah',
            [OddsOption.HTFT_AA]: 'aa',
            [OddsOption.HTFT_AD]: 'ad',
            [OddsOption.HTFT_DH]: 'dh',
            [OddsOption.HTFT_DA]: 'da',
            [OddsOption.HTFT_DD]: 'dd',
            [OddsOption.FGLG_HF]: 'hf',
            [OddsOption.FGLG_HL]: 'hl',
            [OddsOption.FGLG_AF]: 'af',
            [OddsOption.FGLG_AL]: 'al',
            [OddsOption.FGLG_NG]: 'ng',
            [OddsOption.TG_0_1]: '0-1',
            [OddsOption.TG_2_3]: '2-3',
            [OddsOption.TG_4_6]: '4-6',
            [OddsOption.TG_7_Up]: '7-over',
            [OddsOption.CS_0_0]: '0:0',
            [OddsOption.CS_0_1]: '0:1',
            [OddsOption.CS_0_2]: '0:2',
            [OddsOption.CS_0_3]: '0:3',
            [OddsOption.CS_0_4]: '0:4',
            [OddsOption.CS_1_0]: '1:0',
            [OddsOption.CS_1_1]: '1:1',
            [OddsOption.CS_1_2]: '1:2',
            [OddsOption.CS_1_3]: '1:3',
            [OddsOption.CS_1_4]: '1:4',
            [OddsOption.CS_2_0]: '2:0',
            [OddsOption.CS_2_1]: '2:1',
            [OddsOption.CS_2_2]: '2:2',
            [OddsOption.CS_2_3]: '2:3',
            [OddsOption.CS_2_4]: '2:4',
            [OddsOption.CS_3_0]: '3:0',
            [OddsOption.CS_3_1]: '3:1',
            [OddsOption.CS_3_2]: '3:2',
            [OddsOption.CS_3_3]: '3:3',
            [OddsOption.CS_3_4]: '3:4',
            [OddsOption.CS_4_0]: '4:0',
            [OddsOption.CS_4_1]: '4:1',
            [OddsOption.CS_4_2]: '4:2',
            [OddsOption.CS_4_3]: '4:3',
            [OddsOption.CS_4_4]: '4:4',
            [OddsOption.CS_Others]: 'o',
            [OddsOption.DC_1X]: '1x',
            [OddsOption.DC_2X]: '2x',
            [OddsOption.DC_12]: '12',
            [OddsOption.LS_1]: '1',
            [OddsOption.LS_X]: 'x',
            [OddsOption.LS_2]: '2',
            [OddsOption.OR]: 'or',

        };
        return oddsOptionStringMapping[oddsOption] ?? '';
    }

    const homeAwayOptions: Record<string, OddsOption> = { h: OddsOption.Home, a: OddsOption.Away };

    const overUnderOptions: Record<string, OddsOption> = { h: OddsOption.Over, a: OddsOption.Under };

    const oddEvenOptions: Record<string, OddsOption> = { h: OddsOption.Odd, a: OddsOption.Even };

    const _1x2Options: Record<string, OddsOption> = { 1: OddsOption._1X2_1, x: OddsOption._1X2_X, 2: OddsOption._1X2_2 };

    const liveScoreOptions: Record<string, OddsOption> = {
        1: OddsOption.LS_1,
        x: OddsOption.LS_X,
        2: OddsOption.LS_2,
        ls_1: OddsOption.LS_1,
        ls_x: OddsOption.LS_X,
        ls_2: OddsOption.LS_2,
    };

    const correctScoreOptions: Record<string, OddsOption> = {
        '0:0': OddsOption.CS_0_0,
        '0:1': OddsOption.CS_0_1,
        '0:2': OddsOption.CS_0_2,
        '0:3': OddsOption.CS_0_3,
        '0:4': OddsOption.CS_0_4,
        '1:0': OddsOption.CS_1_0,
        '1:1': OddsOption.CS_1_1,
        '1:2': OddsOption.CS_1_2,
        '1:3': OddsOption.CS_1_3,
        '1:4': OddsOption.CS_1_4,
        '2:0': OddsOption.CS_2_0,
        '2:1': OddsOption.CS_2_1,
        '2:2': OddsOption.CS_2_2,
        '2:3': OddsOption.CS_2_3,
        '2:4': OddsOption.CS_2_4,
        '3:0': OddsOption.CS_3_0,
        '3:1': OddsOption.CS_3_1,
        '3:2': OddsOption.CS_3_2,
        '3:3': OddsOption.CS_3_3,
        '3:4': OddsOption.CS_3_4,
        '4:0': OddsOption.CS_4_0,
        '4:1': OddsOption.CS_4_1,
        '4:2': OddsOption.CS_4_2,
        '4:3': OddsOption.CS_4_3,
        '4:4': OddsOption.CS_4_4,
        'o': OddsOption.CS_Others,
    };

    const oddsApiOptionMapping: Partial<Record<EnumMarketType, Record<string, OddsOption>>> = {
        [EnumMarketType.Handicap]: homeAwayOptions,
        [EnumMarketType.FH_Handicap]: homeAwayOptions,
        [EnumMarketType.OverUnder]: overUnderOptions,
        [EnumMarketType.FH_OverUnder]: overUnderOptions,
        [EnumMarketType.OddEven]: oddEvenOptions,
        [EnumMarketType.FH_OddEven]: oddEvenOptions,
        [EnumMarketType._1X2]: _1x2Options,
        [EnumMarketType.FH_1X2]: _1x2Options,
        [EnumMarketType.MoneyLine]: { 1: OddsOption.ML_1, 2: OddsOption.ML_2 },
        [EnumMarketType.HTFT]: {
            hh: OddsOption.HTFT_HH,
            hd: OddsOption.HTFT_HD,
            ha: OddsOption.HTFT_HA,
            dh: OddsOption.HTFT_DH,
            dd: OddsOption.HTFT_DD,
            da: OddsOption.HTFT_DA,
            ah: OddsOption.HTFT_AH,
            ad: OddsOption.HTFT_AD,
            aa: OddsOption.HTFT_AA,
        },
        [EnumMarketType.FGLG]: {
            hf: OddsOption.FGLG_HF,
            hl: OddsOption.FGLG_HL,
            af: OddsOption.FGLG_AF,
            al: OddsOption.FGLG_AL,
            ng: OddsOption.FGLG_NG,
        },
        [EnumMarketType.TotalGoal]: {
            '0-1': OddsOption.TG_0_1,
            '2-3': OddsOption.TG_2_3,
            '4-6': OddsOption.TG_4_6,
            '7-over': OddsOption.TG_7_Up,
        },
        [EnumMarketType.DoubleChance]: {
            '1x': OddsOption.DC_1X,
            'x2': OddsOption.DC_2X,
            '12': OddsOption.DC_12,
        },
        [EnumMarketType.CorrectScore]: correctScoreOptions,
        [EnumMarketType.FH_CorrectScore]: correctScoreOptions,
        [EnumMarketType.SH_CorrectScore]: correctScoreOptions,
        [EnumMarketType.LiveScore]: liveScoreOptions,
        [EnumMarketType.FH_LiveScore]: liveScoreOptions,
        [EnumMarketType.OutRight]: { or: OddsOption.OR },
    };

    export function fromOddsApi(marketType: EnumMarketType, option: string): OddsOption {
        return oddsApiOptionMapping[marketType]?.[option.toLowerCase()] ?? OddsOption.Unknown;
    }
}

export enum PlaceOrderStatus {
    Unknown = 0,
    Success = 1,
    LiveSuccess = 2,
    EventCheckSuccess = 3,
    EventClosed = 4,
    CreditCheckSuccess = 5,
    MemberSuspend = 6,
    MemberClosed = 7,
    HierarachySuspend = 8,
    HierarachyClosed = 9,
    InsufficientCredit = 10,
    BlackListCheckSuccess = 11,
    InBlackList = 12,
    InvalidPrice = 13,
    PriceChange = 14,
    StakeProblem = 15,
    PriceSuspend = 16,
    AccountProblem = 17,
    MaxBetExceed = 18,
    MaxPerMatchExceed = 19,
    PTProblem = 20,
    ExistBetOnSameEvent = 21,
    LessEvent = 22,
    InvalidRefNo = 23,
    IOMWalkinOnly = 24,
    InvalidCookieIdentity = 25,
    SportExcluded = 26,
    HdpPointChange = 27,
    ScoreChanged = 28,
    BettingBudgetExceed = 29,
    MinBetUnMet = 30,
    MPSubBetCountExceed = 31,
    MPMaxPayoutExceed = 32,
    MPMaxAccumulatedStakeForSubEventExceed = 33,
    FailToFulfillBetCountRestrictionForSplitPoint = 34,
    InvalidVoucher = 35,
    InvalidPriceForVoucher = 36,
    InvalidPriceStyle = 37,
    InvalidIp = 38,
    InvalidPriceId = 39,
    PhoneBetting = 40,
    IsNotSingleBet = 41,
    DeductBalanceFail = 42,
    CountryIsBlock = 43,
    InvalidHttpReferer = 44,
    InvalidPriceStyleForVoucher = 45,
    InvalidAccountForVoucher = 46,
    NotValidMixParlayCombination = 47,
    InvalidCurrencyForVietnam = 48,
    PlayerNotLogin = 49,
    LimitExceed = 50,
    MaxWinLoseLimitExceed = 51,
    ThirdPartyTimeout = 52,
    LimitExceedTryLater = 53,
    BetBuilderMaxPayoutExceed = 54,
    BetBuilderMaxAccumulatedStakeExceed = 55,
    LessSelection = 56,
    ExistBetWithSameRefNo = 57,
    IncorrectLiveStatus = 58,
    OrderProcessing = 59,
    RetryOpenTicket = 99,
    GeneralFailure = 255,
}

export namespace PlaceOrderStatus {
    export function fromPollux(status: PolluxPlaceOrderStatus): PlaceOrderStatus {
        const placeOrderStatusMapping: Record<PolluxPlaceOrderStatus, PlaceOrderStatus> = {
            [PolluxPlaceOrderStatus.Unknown]: PlaceOrderStatus.Unknown,
            [PolluxPlaceOrderStatus.Success]: PlaceOrderStatus.Success,
            [PolluxPlaceOrderStatus.LiveSuccess]: PlaceOrderStatus.LiveSuccess,
            [PolluxPlaceOrderStatus.EventCheckSuccess]: PlaceOrderStatus.EventCheckSuccess,
            [PolluxPlaceOrderStatus.EventClosed]: PlaceOrderStatus.EventClosed,
            [PolluxPlaceOrderStatus.CreditCheckSuccess]: PlaceOrderStatus.CreditCheckSuccess,
            [PolluxPlaceOrderStatus.MemberSuspend]: PlaceOrderStatus.MemberSuspend,
            [PolluxPlaceOrderStatus.MemberClosed]: PlaceOrderStatus.MemberClosed,
            [PolluxPlaceOrderStatus.HierarachySuspend]: PlaceOrderStatus.HierarachySuspend,
            [PolluxPlaceOrderStatus.HierarachyClosed]: PlaceOrderStatus.HierarachyClosed,
            [PolluxPlaceOrderStatus.InsufficientCredit]: PlaceOrderStatus.InsufficientCredit,
            [PolluxPlaceOrderStatus.BlackListCheckSuccess]: PlaceOrderStatus.BlackListCheckSuccess,
            [PolluxPlaceOrderStatus.InBlackList]: PlaceOrderStatus.InBlackList,
            [PolluxPlaceOrderStatus.InvalidPrice]: PlaceOrderStatus.InvalidPrice,
            [PolluxPlaceOrderStatus.PriceChange]: PlaceOrderStatus.PriceChange,
            [PolluxPlaceOrderStatus.StakeProblem]: PlaceOrderStatus.StakeProblem,
            [PolluxPlaceOrderStatus.PriceSuspend]: PlaceOrderStatus.PriceSuspend,
            [PolluxPlaceOrderStatus.AccountProblem]: PlaceOrderStatus.AccountProblem,
            [PolluxPlaceOrderStatus.MaxBetExceed]: PlaceOrderStatus.MaxBetExceed,
            [PolluxPlaceOrderStatus.MaxPerMatchExceed]: PlaceOrderStatus.MaxPerMatchExceed,
            [PolluxPlaceOrderStatus.PTProblem]: PlaceOrderStatus.PTProblem,
            [PolluxPlaceOrderStatus.ExistBetOnSameEvent]: PlaceOrderStatus.ExistBetOnSameEvent,
            [PolluxPlaceOrderStatus.LessEvent]: PlaceOrderStatus.LessEvent,
            [PolluxPlaceOrderStatus.InvalidRefNo]: PlaceOrderStatus.InvalidRefNo,
            [PolluxPlaceOrderStatus.IOMWalkinOnly]: PlaceOrderStatus.IOMWalkinOnly,
            [PolluxPlaceOrderStatus.InvalidCookieIdentity]: PlaceOrderStatus.InvalidCookieIdentity,
            [PolluxPlaceOrderStatus.SportExcluded]: PlaceOrderStatus.SportExcluded,
            [PolluxPlaceOrderStatus.HdpPointChange]: PlaceOrderStatus.HdpPointChange,
            [PolluxPlaceOrderStatus.ScoreChanged]: PlaceOrderStatus.ScoreChanged,
            [PolluxPlaceOrderStatus.BettingBudgetExceed]: PlaceOrderStatus.BettingBudgetExceed,
            [PolluxPlaceOrderStatus.MinBetUnMet]: PlaceOrderStatus.MinBetUnMet,
            [PolluxPlaceOrderStatus.MPSubBetCountExceed]: PlaceOrderStatus.MPSubBetCountExceed,
            [PolluxPlaceOrderStatus.MPMaxPayoutExceed]: PlaceOrderStatus.MPMaxPayoutExceed,
            [PolluxPlaceOrderStatus.MPMaxAccumulatedStakeForSubEventExceed]: PlaceOrderStatus.MPMaxAccumulatedStakeForSubEventExceed,
            [PolluxPlaceOrderStatus.FailToFulfillBetCountRestrictionForSplitPoint]: PlaceOrderStatus.FailToFulfillBetCountRestrictionForSplitPoint,
            [PolluxPlaceOrderStatus.InvalidVoucher]: PlaceOrderStatus.InvalidVoucher,
            [PolluxPlaceOrderStatus.InvalidPriceForVoucher]: PlaceOrderStatus.InvalidPriceForVoucher,
            [PolluxPlaceOrderStatus.InvalidPriceStyle]: PlaceOrderStatus.InvalidPriceStyle,
            [PolluxPlaceOrderStatus.InvalidIp]: PlaceOrderStatus.InvalidIp,
            [PolluxPlaceOrderStatus.InvalidPriceId]: PlaceOrderStatus.InvalidPriceId,
            [PolluxPlaceOrderStatus.PhoneBetting]: PlaceOrderStatus.PhoneBetting,
            [PolluxPlaceOrderStatus.IsNotSingleBet]: PlaceOrderStatus.IsNotSingleBet,
            [PolluxPlaceOrderStatus.DeductBalanceFail]: PlaceOrderStatus.DeductBalanceFail,
            [PolluxPlaceOrderStatus.CountryIsBlock]: PlaceOrderStatus.CountryIsBlock,
            [PolluxPlaceOrderStatus.InvalidHttpReferer]: PlaceOrderStatus.InvalidHttpReferer,
            [PolluxPlaceOrderStatus.InvalidPriceStyleForVoucher]: PlaceOrderStatus.InvalidPriceStyleForVoucher,
            [PolluxPlaceOrderStatus.InvalidAccountForVoucher]: PlaceOrderStatus.InvalidAccountForVoucher,
            [PolluxPlaceOrderStatus.NotValidMixParlayCombination]: PlaceOrderStatus.NotValidMixParlayCombination,
            [PolluxPlaceOrderStatus.InvalidCurrencyForVietnam]: PlaceOrderStatus.InvalidCurrencyForVietnam,
            [PolluxPlaceOrderStatus.PlayerNotLogin]: PlaceOrderStatus.PlayerNotLogin,
            [PolluxPlaceOrderStatus.LimitExceed]: PlaceOrderStatus.LimitExceed,
            [PolluxPlaceOrderStatus.MaxWinLoseLimitExceed]: PlaceOrderStatus.MaxWinLoseLimitExceed,
            [PolluxPlaceOrderStatus.ThirdPartyTimeout]: PlaceOrderStatus.ThirdPartyTimeout,
            [PolluxPlaceOrderStatus.LimitExceedTryLater]: PlaceOrderStatus.LimitExceedTryLater,
            [PolluxPlaceOrderStatus.RetryOpenTicket]: PlaceOrderStatus.RetryOpenTicket,
            [PolluxPlaceOrderStatus.GeneralFailure]: PlaceOrderStatus.GeneralFailure,
            [PolluxPlaceOrderStatus.BBMaxPayoutExceed]: PlaceOrderStatus.BetBuilderMaxPayoutExceed,
            [PolluxPlaceOrderStatus.BBMaxAccumulatedStakeExceed]: PlaceOrderStatus.BetBuilderMaxAccumulatedStakeExceed,
            [PolluxPlaceOrderStatus.LessSelection]: PlaceOrderStatus.LessSelection,
            [PolluxPlaceOrderStatus.ExistBetWithSameRefNo]: PlaceOrderStatus.ExistBetWithSameRefNo,
            [PolluxPlaceOrderStatus.IncorrectLiveStatus]: PlaceOrderStatus.IncorrectLiveStatus,
            [PolluxPlaceOrderStatus.OrderProcessing]: PlaceOrderStatus.OrderProcessing,
        };
        if (placeOrderStatusMapping[status] === undefined) {
            console.error(`transform place order status from pollux error: ${status}`);
            return PlaceOrderStatus.Unknown;
        }
        return placeOrderStatusMapping[status];
    }
}

export enum LiveStreamResource {
    BetRadar = 1,
    GLive = 2,
    BetConstruct = 3,
    BetGenius = 4,
}

export namespace LiveStreamResource {
    export function toPollux(resource: LiveStreamResource): PolluxLiveStreamResource {
        const liveStreamResourceMapping: Record<LiveStreamResource, PolluxLiveStreamResource> = {
            [LiveStreamResource.BetRadar]: PolluxLiveStreamResource.BetRadar,
            [LiveStreamResource.GLive]: PolluxLiveStreamResource.GLive,
            [LiveStreamResource.BetConstruct]: PolluxLiveStreamResource.BetConstruct,
            [LiveStreamResource.BetGenius]: PolluxLiveStreamResource.BetGenius,
        };
        if (liveStreamResourceMapping[resource] === undefined) {
            throw new Error(`transform live stream resource to pollux error: ${resource}`);
        }
        return liveStreamResourceMapping[resource];
    }
}

export enum BetPageType {
    Unknown = 0,
    Main = 201,
    Live = 202,
    Today = 203,
    EarlyMarket = 204,
    MixParlay = 205,
    Outright = 206,
    MatchDetail = 207,
    LiveCourt = 208,
    LiveStream = 209,
    ProductPlatform = 210,
    MyMatches = 211,
    ComboParlay = 212,
    Widget = 214,
    SportsCup = 215,
    EFootball = 216,
    MultipleSportsZone = 217,
    LeagueZone = 218,
    LocalPage = 219,
    TournamentCenter_FeaturedMatches = 220,
    TournamentCenter_Groups = 221,
    TournamentCenter_Knockout = 222,
    TournamentCenter_UpcomingMatches = 223,
    TournamentCenter_Outright = 224,
    TournamentCenter_Matches = 225,
    MpCombination = 226,
    MpCombination_ComboParlay = 227,
    RepeatBet = 228,
    TournamentCenter_Country = 229,
    TournamentCenter_Prediction = 230,
    BetBuilderRecommendation = 231,
    SimulationSportsZone = 232,
    BetBuilderRecommendation_MoreMarket = 233,
    BetBuilderRecommendation_BetBuilderTab = 234,
    BetBuilderRecommendation_MatchDetails = 235,
    SingleModeParlay_Medium = 236,
    SingleModeParlay_Big = 237,
    SingleModeComboParlay_Big = 238,
}

export enum DisplaySection {
    Unknown = 0,
    TournamentCenter_FeaturedMatches = 2,
    TournamentCenter_Groups = 3,
    TournamentCenter_Knockout = 4,
    TournamentCenter_UpcomingMatches = 5,
    TournamentCenter_Outright = 6,
    MpCombination = 7,
    ParlayMode = 8,
    TournamentCenter_Country = 9,
    TournamentCenter_Matches = 10,
    TournamentCenter_Prediction = 11,
}

export enum FGLGResult {
    Unknown = 0,
    Home = 1,
    Away = 2,
    NoGoal = 3,
}

export namespace FGLGResult {
    export function fromPollux(result: EnumResultFGLG): FGLGResult {
        switch (result) {
            case EnumResultFGLG.Unknown:
                return FGLGResult.Unknown;
            case EnumResultFGLG.Home:
                return FGLGResult.Home;
            case EnumResultFGLG.Away:
                return FGLGResult.Away;
            case EnumResultFGLG.NoGoal:
                return FGLGResult.NoGoal;
            default: console.error(`transform FGLG result from pollux error: ${result}`); return FGLGResult.Unknown;
        }
    }
}

export enum ScoringType {
    Unknown,
    Football,
    Golf,
    Badminton,
    BeachSoccer,
    TableTennis,
    BasketBall,
    Tennis,
    MotorSport,
    WinterSport,
    Cricket,
}

export namespace ScoringType {
    export function fromPollux(scoringType: EnumScoringType): ScoringType {
        const scoringTypeMapping: Record<EnumScoringType, ScoringType> = {
            [EnumScoringType.Unknown]: ScoringType.Unknown,
            [EnumScoringType.Football]: ScoringType.Football,
            [EnumScoringType.Golf]: ScoringType.Golf,
            [EnumScoringType.Badminton]: ScoringType.Badminton,
            [EnumScoringType.BeachSoccer]: ScoringType.BeachSoccer,
            [EnumScoringType.TableTennis]: ScoringType.TableTennis,
            [EnumScoringType.BasketBall]: ScoringType.BasketBall,
            [EnumScoringType.Tennis]: ScoringType.Tennis,
            [EnumScoringType.MotorSport]: ScoringType.MotorSport,
            [EnumScoringType.WinterSport]: ScoringType.WinterSport,
            [EnumScoringType.Cricket]: ScoringType.Cricket,
        };
        return scoringTypeMapping[scoringType];
    }
}

export enum ResultType {
    HT = 'HT',
    FT = 'FT',
    TotalPointWin = 'TotalPointWin',
    TotalGamesWin = 'TotalGamesWin',
    TotalSetsWin = 'TotalSetsWin',
}

export enum ResultColumnType {
    FtHt,
    Winner,
    G5PointSet,
    G5PointGame,
    G5GameSet,
    G7PointSet,
    FinalScore,
    P3FinalScore,
    WinnerRound,
    Result,
    Unknown,
}

export enum GameFrom {
    Unknown = -1,
    SportsAsiaGameBanner = 10202,
    SportsAsiaCarouselBanner = 10206,
    SportsAsiaLeftSideBanner = 10207,
    GamesDesktopBanner = 10302,
    GamesMobileBanner = 20302,
    SyllisSportsToday = 20705,
    SyllisBsi = 20799,
    TeraMobileSportsBanner = 20815,
    TeraMobileLandingBanner = 20816,
    NativeAppLandingBanner = 60016,
    SportMobileTopBannerPromotion = 20735,
}

export namespace GameFrom {
    export function fromPollux(gameFrom: EnumGameFrom): GameFrom {
        const gameFromMapping: Record<EnumGameFrom, GameFrom> = {
            [EnumGameFrom.Unknown]: GameFrom.Unknown,
            [EnumGameFrom.SportsAsiaGameBanner]: GameFrom.SportsAsiaGameBanner,
            [EnumGameFrom.SportsAsiaCarouselBanner]: GameFrom.SportsAsiaCarouselBanner,
            [EnumGameFrom.SportsAsiaLeftSideBanner]: GameFrom.SportsAsiaLeftSideBanner,
            [EnumGameFrom.GamesDesktopBanner]: GameFrom.GamesDesktopBanner,
            [EnumGameFrom.GamesMobileBanner]: GameFrom.GamesMobileBanner,
            [EnumGameFrom.SyllisSportsToday]: GameFrom.SyllisSportsToday,
            [EnumGameFrom.SyllisBsi]: GameFrom.SyllisBsi,
            [EnumGameFrom.TeraMobileSportsBanner]: GameFrom.TeraMobileSportsBanner,
            [EnumGameFrom.TeraMobileLandingBanner]: GameFrom.TeraMobileLandingBanner,
            [EnumGameFrom.NativeAppLandingBanner]: GameFrom.NativeAppLandingBanner,
        };
        if (gameFromMapping[gameFrom] === undefined) {
            console.error(`transform game from from pollux error: ${gameFrom}`);
            return GameFrom.Unknown;
        }
        return gameFromMapping[gameFrom];
    }
}

export enum DisplayTime {
    SystemTime = 0,
    LocalTime = 1,
}

export namespace DisplayTime {
    export function fromPollux(displayTime: PolluxDisplayTime): DisplayTime {
        const displayTimeMapping: Record<PolluxDisplayTime, DisplayTime> = {
            [PolluxDisplayTime.SystemTime]: DisplayTime.SystemTime,
            [PolluxDisplayTime.LocalTime]: DisplayTime.LocalTime,
        };
        if (displayTimeMapping[displayTime] === undefined) {
            console.error(`transform game from from pollux error: ${displayTime}`);
            return DisplayTime.SystemTime;
        }
        return displayTimeMapping[displayTime];
    }
}

export enum OddsSorting {
    Unknown = 0,
    Normal = 1,
    ShowTime = 2,
}

export enum OddsDisplayMode {
    Unknown = 0,
    Double = 1,
    Single = 2,
    Simple = 3,
}

export enum StatementDetailStatus {
    Unknown = 0,
    Running = 1,
    Waiting = 2,
    Won = 3,
    Lose = 4,
    Draw = 5,
    Rejected = 6,
    Void = 7,
    Refunded = 8,
    SpecialVoid = 9,
    HalfWon = 10,
    HalfLose = 11,
    SuspendVoid = 12,
    DangerousMpVoid = 13,
    CashOut = 16,
    CashOutBet = 32,
}

export namespace StatementDetailStatus {
    export function fromPollux(status: TiEnumStatementDetailStatus): StatementDetailStatus {
        const statementDetailStatusMapping: Record<TiEnumStatementDetailStatus, StatementDetailStatus> = {
            [TiEnumStatementDetailStatus.Unknown]: StatementDetailStatus.Unknown,
            [TiEnumStatementDetailStatus.Running]: StatementDetailStatus.Running,
            [TiEnumStatementDetailStatus.Waiting]: StatementDetailStatus.Waiting,
            [TiEnumStatementDetailStatus.Won]: StatementDetailStatus.Won,
            [TiEnumStatementDetailStatus.Lose]: StatementDetailStatus.Lose,
            [TiEnumStatementDetailStatus.Draw]: StatementDetailStatus.Draw,
            [TiEnumStatementDetailStatus.Rejected]: StatementDetailStatus.Rejected,
            [TiEnumStatementDetailStatus.Void]: StatementDetailStatus.Void,
            [TiEnumStatementDetailStatus.Refunded]: StatementDetailStatus.Refunded,
            [TiEnumStatementDetailStatus.Special_Void]: StatementDetailStatus.SpecialVoid,
            [TiEnumStatementDetailStatus.HalfWon]: StatementDetailStatus.HalfWon,
            [TiEnumStatementDetailStatus.HalfLose]: StatementDetailStatus.HalfLose,
            [TiEnumStatementDetailStatus.SuspendVoid]: StatementDetailStatus.SuspendVoid,
            [TiEnumStatementDetailStatus.Dangerous_Mp_Void]: StatementDetailStatus.DangerousMpVoid,
            [TiEnumStatementDetailStatus.CashOut]: StatementDetailStatus.CashOut,
            [TiEnumStatementDetailStatus.CashOutBet]: StatementDetailStatus.CashOutBet,
        };
        if (statementDetailStatusMapping[status] === undefined) {
            console.error(`transform statement detail status from pollux error: ${status}`);
            return StatementDetailStatus.Unknown;
        }
        return statementDetailStatusMapping[status];
    }
}

export enum LanguageType {
    EN = 'EN',
    ZH_CN = 'ZH_CN',
    JA_JP = 'JA_JP',
    KO_KR = 'KO_KR',
    TH_TH = 'TH_TH',
    VI_VN = 'VI_VN',
    ID_ID = 'ID_ID',
    MY_MM = 'MY_MM',
    PT_BR = 'PT_BR',
    HI_IN = 'HI_IN',
    TA_IN = 'TA_IN',
    KM_KH = 'KM_KH',
    BN_BD = 'BN_BD',
    MS_MY = 'MS_MY'
}

export namespace LanguageType {
    export function toOddsApi(language: LanguageType): EnumLanguageType {
        const languageTypeMapping: Record<LanguageType, EnumLanguageType> = {
            [LanguageType.EN]: EnumLanguageType.EN,
            [LanguageType.ZH_CN]: EnumLanguageType.ZH_CN,
            [LanguageType.JA_JP]: EnumLanguageType.JA_JP,
            [LanguageType.KO_KR]: EnumLanguageType.KO_KR,
            [LanguageType.TH_TH]: EnumLanguageType.TH_TH,
            [LanguageType.VI_VN]: EnumLanguageType.VI_VN,
            [LanguageType.ID_ID]: EnumLanguageType.ID_ID,
            [LanguageType.MY_MM]: EnumLanguageType.MY_MM,
            [LanguageType.PT_BR]: EnumLanguageType.PT_BR,
            [LanguageType.HI_IN]: EnumLanguageType.HI_IN,
            [LanguageType.TA_IN]: EnumLanguageType.TA_IN,
            [LanguageType.KM_KH]: EnumLanguageType.KM_KH,
            [LanguageType.BN_BD]: EnumLanguageType.BN_BD,
            [LanguageType.MS_MY]: EnumLanguageType.MS_MY,
        };

        if (languageTypeMapping[language] === undefined) {
            throw new Error(`transform language type to odds api error: ${language}`);
        }
        return languageTypeMapping[language];
    }

    export function fromPlatform(language: string): LanguageType {
        const platformLanguageMapping: Record<string, LanguageType> = {
            EN_US: LanguageType.EN,
            ZH_CN: LanguageType.ZH_CN,
            JA_JP: LanguageType.JA_JP,
            KO_KR: LanguageType.KO_KR,
            TH_TH: LanguageType.TH_TH,
            VI_VN: LanguageType.VI_VN,
            ID_ID: LanguageType.ID_ID,
            MY_MM: LanguageType.MY_MM,
            PT_BR: LanguageType.PT_BR,
            HI_IN: LanguageType.HI_IN,
            TA_IN: LanguageType.TA_IN,
            KM_KH: LanguageType.KM_KH,
            BN_BD: LanguageType.BN_BD,
            MS_MY: LanguageType.MS_MY,
        };
        const normalized = language.replace('-', '_').toUpperCase();
        return platformLanguageMapping[normalized] ?? LanguageType.EN;
    }

    export function fromOddsApi(language: EnumLanguageType): LanguageType {
        switch (language) {
            case EnumLanguageType.EN: return LanguageType.EN;
            case EnumLanguageType.ID_ID: return LanguageType.ID_ID;
            case EnumLanguageType.JA_JP: return LanguageType.JA_JP;
            case EnumLanguageType.KO_KR: return LanguageType.KO_KR;
            case EnumLanguageType.MY_MM: return LanguageType.MY_MM;
            case EnumLanguageType.TH_TH: return LanguageType.TH_TH;
            case EnumLanguageType.VI_VN: return LanguageType.VI_VN;
            case EnumLanguageType.ZH_CN: return LanguageType.ZH_CN;
            case EnumLanguageType.ZH_TW: return LanguageType.ZH_CN;
            case EnumLanguageType.PT_BR: return LanguageType.PT_BR;
            case EnumLanguageType.KM_KH: return LanguageType.KM_KH;
            case EnumLanguageType.BN_BD: return LanguageType.BN_BD;
            case EnumLanguageType.HI_IN: return LanguageType.HI_IN;
            case EnumLanguageType.TA_IN: return LanguageType.TA_IN;
            case EnumLanguageType.MS_MY: return LanguageType.MS_MY;
            default:
                console.warn(`Failed to transform LanguageType fromOddsApi: ${language}`);
                return LanguageType.EN;
        }
    }

}

export function toLiveCourt(lang: LanguageType): string {
    switch (lang) {
        case LanguageType.EN: return 'en';
        case LanguageType.ID_ID: return 'id';
        case LanguageType.JA_JP: return 'ja';
        case LanguageType.KO_KR: return 'ko';
        case LanguageType.TH_TH: return 'th';
        case LanguageType.VI_VN: return 'vi';
        case LanguageType.ZH_CN: return 'zh';
        case LanguageType.PT_BR: return 'pt';
        case LanguageType.KM_KH: return 'km';
        case LanguageType.MS_MY: return 'en';
        default: return 'en';
    }
}

export function toStatistics(lang: LanguageType): string {
    switch (lang) {
        case LanguageType.ZH_CN: return 'zh';
        case LanguageType.TH_TH: return 'th';
        case LanguageType.VI_VN: return 'vi';
        case LanguageType.ID_ID: return 'id';
        case LanguageType.JA_JP: return 'ja';
        case LanguageType.KO_KR: return 'ko';
        default: return 'en';
    }
}

export enum AbTestGroup {
    LocalPage = 89,
    SingleModeBetSlipTbCurrency = 67,
    SingleModeBetSlipPpCurrency = 68,
}

export enum TournamentSize {
    Big,
    Medium,
    Small,
    Unknown,
}

export namespace TournamentSize {
    export function fromOddsApi(tournamentSize: EnumTournamentSizeType): TournamentSize {
        const tournamentSizeMapping: Record<EnumTournamentSizeType, TournamentSize> = {
            [EnumTournamentSizeType.Big]: TournamentSize.Big,
            [EnumTournamentSizeType.Medium]: TournamentSize.Medium,
            [EnumTournamentSizeType.Small]: TournamentSize.Small,
        };
        if (tournamentSizeMapping[tournamentSize] === undefined) {
            throw new Error(`transform tournament size from odds api error: ${tournamentSize}`);
        }
        return tournamentSizeMapping[tournamentSize];
    }
}

export enum BetStatus2 {
    Unknown = 0,
    Running = 1,
    Waiting = 2,
    Won = 3,
    Lose = 4,
    Draw = 5,
    Rejected = 6,
    Void = 7,
    Refunded = 8,
    Special_Void = 9,
    Dangerous_Mp_Void = 13,
    Cashed_Out = 16,
    Cash_Out = 32,
    PartialCashOut = 64,
    I_O_M = 256,
    Special = 512,
    DelaySpecialVoid = 521,
    Dangerous_1 = 1024,
    Dangerous_2 = 2048,
    Dangerous_3 = 4096,
    Cash = 8192,
    Indirect = 16384,
    System_Void = 32768,
    Early_Settled = 65536,
    Delay_Void = 131072,
    TestMode = 262144,
    Resettled = 524288,
    ThirdParty = 1048576,
    FreeBet = 67108864,
    GhostTicket = 134217728,
    MuayStep = 268435456,
    PremiumCricket = 536870912,
    ZeroCommission = 1073741824,
}

export enum BetBuilderMarket{
    Unknown = 0,
    Set1x2 = 1,
    SetDoubleChance = 2,
    SetTotalGoalsOverUnder = 3,
    SetTotalGoalsOddEven = 4,
    SetCorrectScore = 5,
    SetExactNumberGoals = 6,
    SetHandicap = 47,
    SetTotalCornersHandicap = 49,
    SetTotalCornersOverUnder = 50,
    SetTotalCornersOddEven = 51,
    SetTotalCorners1x2 = 52,
}

export enum BetBuilderStatType{
    Unknown = 0,
    FullTime = 1,
    FirstHalf = 2,
    SecondHalf = 3,
    FullTimeCorners = 4,
    FirstHalfCorners = 5,
    SecondHalfCorners = 6,
}

export enum BetBuilderSelectionType{
    Unknown = 0,
    _1 = 1,
    _2 = 2,
    X = 3,
    Odd = 4,
    Even = 5,
    _1X = 6,
    _2X = 7,
    _12 = 8,
    Over = 9,
    Under = 10,
    TotalGoal = 18,
    Home = 229,
    Away = 230,

}

export enum BetBuilderDisplayType {
    Unknown = 0,
    Handicap = 1,
    OddEven = 2,
    OverUnder = 3,
    CorrectScore = 4,
    _1X2 = 5,
    TotalGoal = 6,
    FH_Handicap = 7,
    FH_1X2 = 8,
    FH_OverUnder = 9,
    HTFT = 10,
    MoneyLine = 11,
    FH_OddEven = 12,
    FGLG = 13,
    FH_CorrectScore = 14,
    DoubleChance = 15,
    LiveScore = 16,
    FH_LiveScore = 17,
    SH_CorrectScore = 19,
    Game = 38,
    OutRight = 39,
    MixParlay = 40,
}

export namespace BetBuilderDisplayType {
    export function fromOddsApi(displayType: EnumDisplayType) {
        const mapping: Record<EnumDisplayType, BetBuilderDisplayType> = {
            [EnumDisplayType.Unknown]: BetBuilderDisplayType.Unknown,
            [EnumDisplayType.Handicap]: BetBuilderDisplayType.Handicap,
            [EnumDisplayType.OddEven]: BetBuilderDisplayType.OddEven,
            [EnumDisplayType.OverUnder]: BetBuilderDisplayType.OverUnder,
            [EnumDisplayType.CorrectScore]: BetBuilderDisplayType.CorrectScore,
            [EnumDisplayType._1X2]: BetBuilderDisplayType._1X2,
            [EnumDisplayType.TotalGoal]: BetBuilderDisplayType.TotalGoal,
            [EnumDisplayType.FhHandicap]: BetBuilderDisplayType.FH_Handicap,
            [EnumDisplayType.Fh1X2]: BetBuilderDisplayType.FH_1X2,
            [EnumDisplayType.FhOverUnder]: BetBuilderDisplayType.FH_OverUnder,
            [EnumDisplayType.Htft]: BetBuilderDisplayType.HTFT,
            [EnumDisplayType.MoneyLine]: BetBuilderDisplayType.MoneyLine,
            [EnumDisplayType.FhOddEven]: BetBuilderDisplayType.FH_OddEven,
            [EnumDisplayType.Fglg]: BetBuilderDisplayType.FGLG,
            [EnumDisplayType.FhCorrectScore]: BetBuilderDisplayType.FH_CorrectScore,
            [EnumDisplayType.DoubleChance]: BetBuilderDisplayType.DoubleChance,
            [EnumDisplayType.LiveScore]: BetBuilderDisplayType.LiveScore,
            [EnumDisplayType.FhLiveScore]: BetBuilderDisplayType.FH_LiveScore,
            [EnumDisplayType.ShCorrectScore]: BetBuilderDisplayType.SH_CorrectScore,
            [EnumDisplayType.Game]: BetBuilderDisplayType.Game,
            [EnumDisplayType.OutRight]: BetBuilderDisplayType.OutRight,
            [EnumDisplayType.MixParlay]: BetBuilderDisplayType.MixParlay,
        };

        return mapping[displayType] ?? BetBuilderDisplayType.Unknown;
    }
}

export enum BetBuilderSelectionStatus {
    Unknown = 0,
    Running = 1,
    Suspended = 2,
    Internal = 3,
    Closed = 4,
    Incompatible = 5,
}

export namespace BetBuilderSelectionStatus {
    export function fromOddsApi(status: EnumSelectionStatus) {
        const mapping: Record<EnumSelectionStatus, BetBuilderSelectionStatus> = {
            [EnumSelectionStatus.Unknown]: BetBuilderSelectionStatus.Unknown,
            [EnumSelectionStatus.Running]: BetBuilderSelectionStatus.Running,
            [EnumSelectionStatus.Suspended]: BetBuilderSelectionStatus.Suspended,
            [EnumSelectionStatus.Internal]: BetBuilderSelectionStatus.Internal,
            [EnumSelectionStatus.Closed]: BetBuilderSelectionStatus.Closed,
        };

        return mapping[status] ?? BetBuilderSelectionStatus.Unknown;
    }

    export function fromPollux(status: PolluxSelectionStatus) {
        const mapping: Record<PolluxSelectionStatus, BetBuilderSelectionStatus> = {
            [PolluxSelectionStatus.Unknown]: BetBuilderSelectionStatus.Unknown,
            [PolluxSelectionStatus.Running]: BetBuilderSelectionStatus.Running,
            [PolluxSelectionStatus.Suspended]: BetBuilderSelectionStatus.Suspended,
            [PolluxSelectionStatus.Internal]: BetBuilderSelectionStatus.Internal,
            [PolluxSelectionStatus.Closed]: BetBuilderSelectionStatus.Closed,
            [PolluxSelectionStatus.Incompatible]: BetBuilderSelectionStatus.Incompatible,
        };
        return mapping[status] ?? BetBuilderSelectionStatus.Unknown;
    }
}

export enum BetBuilderDisplayNameType {
    None,
    Short,
    Full,
}

export enum MarketTab {
    Sbo,
    BetBuilder,
}

export enum SelectionOutcome {
    NoOutcome = 0,
    WON = 1,
    LOSE = 2,
    REFUND = 3,
    DRAW = 4,
    HALF_WIN = 6,
    HALF_LOSE = 7,
    VOID_SUSPENDED_MATCH = 9,
}

export enum PriceChangeStatus {
    Rise,
    Drop,
    Remain
}

export namespace SelectionOutcome {
    export function fromPollux(outcome: PolluxSelectionOutcome) {
        const mapping: Record<PolluxSelectionOutcome, SelectionOutcome> = {
            [PolluxSelectionOutcome.NoOutcome]: SelectionOutcome.NoOutcome,
            [PolluxSelectionOutcome.WON]: SelectionOutcome.WON,
            [PolluxSelectionOutcome.LOSE]: SelectionOutcome.LOSE,
            [PolluxSelectionOutcome.REFUND]: SelectionOutcome.REFUND,
            [PolluxSelectionOutcome.DRAW]: SelectionOutcome.DRAW,
            [PolluxSelectionOutcome.HALF_WIN]: SelectionOutcome.HALF_WIN,
            [PolluxSelectionOutcome.HALF_LOSE]: SelectionOutcome.HALF_LOSE,
            [PolluxSelectionOutcome.VOID_SUSPENDED_MATCH]: SelectionOutcome.VOID_SUSPENDED_MATCH,
        };

        return mapping[outcome] ?? SelectionOutcome.NoOutcome;
    }
}

export enum BetBuilderTicketStatus {
    Unknown = 0,
    Available = 1,
    Unavailable = 2,
    InsufficientLegs = 3,
    MaxLegCountExceeded = 4,
    RedundantSelection = 5,
    IncompatibleSelection = 6,
}

export namespace BetBuilderTicketStatus {
    export function fromPollux(status: CompositeMarketStatus) {
        const mapping: Record<CompositeMarketStatus, BetBuilderTicketStatus> = {
            [CompositeMarketStatus.Unknown]: BetBuilderTicketStatus.Unknown,
            [CompositeMarketStatus.Available]: BetBuilderTicketStatus.Available,
            [CompositeMarketStatus.Unavailable]: BetBuilderTicketStatus.Unavailable,
            [CompositeMarketStatus.InsufficientLegs]: BetBuilderTicketStatus.InsufficientLegs,
            [CompositeMarketStatus.MaxLegCountExceeded]: BetBuilderTicketStatus.MaxLegCountExceeded,
            [CompositeMarketStatus.RedundantSelection]: BetBuilderTicketStatus.RedundantSelection,
            [CompositeMarketStatus.IncompatibleSelection]: BetBuilderTicketStatus.IncompatibleSelection,
        };

        return mapping[status] ?? BetBuilderTicketStatus.Unknown;
    }
}

export enum TextAlign{
    Left = 'left',
    Center = 'center',
    Right = 'right',
}

export enum HashType {
    MD5,
    SHA256,
}

export enum BetRadarStreamStatus {
    Approved = 'Approved',
    OnAirReady = 'On Air Ready',
    OnAirBroadcast = 'On Air Broadcast',
}

export enum ResultPageTab {
    MatchResults,
    OutrightResults
}

export enum EventSettleStatus {
    None = 0,
    Settled = 1,
    SettledMultiple = 2,
    Refunded = 4,
    RefundedMultiple = 8,
    SettledCancelled = 16,
    HTSettlement = 32,
    FTSettlement = 64,
    FTRefunded = 128,
    HTSettledMultiple = 256,
    FTSuspendedMatch = 512,
    FHSuspendedMatch = 1024,
    FTCancelled = 2048,
    FHCancelled = 4096,
    FTPostponed = 8192,
    FHPostponed = 16384,
    FTAbandoned = 32768,
    FHAbandoned = 65536,
    FTCompleteEarly = 131072,
    FHCompleteEarly = 262144,
    Pending = 524288,
}

export enum ResultsDateFilter {
    Today,
    Minus1,
    Minus2,
    Minus3,
}

export enum OutrightResultsDateFilter {
    OneWeek,
    TwoWeek,
    OneMonth,
}

export enum ScoreDisplayType{
    Score,
    Result,
    Winner,
}

export enum SboCurrency {
    SD$ = 'SD$',
    RM = 'RM',
    US$ = 'US$',
    TB = 'TB',
    HK$ = 'HK$',
    EURO = 'EURO',
    PP = 'Pp',
    AUD = 'AUD',
    GBP = 'GBP',
    RMB = 'RMB',
    BDT = 'BDT',
    PESO = 'PESO',
    NIO = 'NIO',
    YEN = 'YEN',
    USD = 'USD',
    LKR = 'LKR',
    PHP = 'php',
    ZAR = 'ZAR',
    KRW = 'KRW',
    CAD = 'CAD',
    NZD = 'NZD',
    NOK = 'NOK',
    SEK = 'SEK',
    CHF = 'CHF',
    BND = 'BND',
    VND = 'VND',
    MMK = 'MMK',
    TMP = 'TMP',
    ZWD = 'ZWD',
    INR = 'INR',
    CNY = 'CNY',
    HKD = 'HKD',
    MYR = 'MYR',
    RR = 'RR',
    USDT = 'USDT',
    BRL = 'BRL',
    KHR = 'KHR',
}

export enum MyBetsTab {
    All = 'all',
    Running = 'running',
    Settled = 'settled',
    Rejected = 'rejected',
}

export enum TournamentStage {
    Unknown = 0,
    Final = 1,
    ThirdPlace = 2,
    SemiFinals = 3,
    QuarterFinals = 4,
    RoundOf16 = 5,
    RoundOf32 = 6,
    Group = 7,
}

export namespace TournamentStage {
    export function fromOddsApi(oddsApiTournamentStage: EnumTournamentStage): TournamentStage {
        const tournamentStageMapping: Record<EnumTournamentStage, TournamentStage> = {
            [EnumTournamentStage.Unknown]: TournamentStage.Unknown,
            [EnumTournamentStage.Final]: TournamentStage.Final,
            [EnumTournamentStage.ThirdPlace]: TournamentStage.ThirdPlace,
            [EnumTournamentStage.SemiFinals]: TournamentStage.SemiFinals,
            [EnumTournamentStage.QuarterFinals]: TournamentStage.QuarterFinals,
            [EnumTournamentStage.RoundOf16]: TournamentStage.RoundOf16,
            [EnumTournamentStage.RoundOf32]: TournamentStage.RoundOf32,
        };
        return tournamentStageMapping[oddsApiTournamentStage] ?? TournamentStage.Unknown;
    }
}

export enum EventStatus {
    Unknown = 0,
    Running = 1,
    Internal = 2,
    Closed = 3,
    Completed = 4,
}

export namespace EventStatus {
    export function fromOddsApi(eventStatus: TiEnumEventStatus): EventStatus {
        const eventStatusMapping: Record<TiEnumEventStatus, EventStatus> = {
            [TiEnumEventStatus.Unknown]: EventStatus.Unknown,
            [TiEnumEventStatus.Running]: EventStatus.Running,
            [TiEnumEventStatus.Internal]: EventStatus.Internal,
            [TiEnumEventStatus.Closed]: EventStatus.Closed,
            [TiEnumEventStatus.Completed]: EventStatus.Completed,
        };

        return eventStatusMapping[eventStatus] ?? EventStatus.Unknown;
    }
}

export enum TournamentCenterTab {
    Overview,
    Countries,
    Matches,
    Promotion,
}

export enum TournamentOverviewTab {
    Groups,
    Knockout
}

export enum TimezoneOffset {
    Server = -4,
    Singapore = 8,
}

export enum CashOutSortOption {
    Time = 1,
    HighestProfit = 2,
}

export enum PoolMaxEntries {
    Unlimited = 'unlimited',
    Two = 'h2h',
    Three = '3players',
    Six = '6players',
}

export enum FantasySelectionMyBetsTab {
    All = 'all',
    Running = 'running',
    Settled = 'settled',
}

export enum FantasySelectionBetStatus {
    Running,
    Won,
    Lose,
    Refund,
    Amended,
    Reversed,
}

export namespace FantasySelectionBetStatus {
    export function fromPollux(status: PolluxFantasySelectionBetStatus): FantasySelectionBetStatus {
        const statusMapping: Record<PolluxFantasySelectionBetStatus, FantasySelectionBetStatus> = {
            [PolluxFantasySelectionBetStatus.Running]: FantasySelectionBetStatus.Running,
            [PolluxFantasySelectionBetStatus.Won]: FantasySelectionBetStatus.Won,
            [PolluxFantasySelectionBetStatus.Lost]: FantasySelectionBetStatus.Lose,
            [PolluxFantasySelectionBetStatus.Refunded]: FantasySelectionBetStatus.Refund,
            [PolluxFantasySelectionBetStatus.Amended]: FantasySelectionBetStatus.Amended,
            [PolluxFantasySelectionBetStatus.Reversed]: FantasySelectionBetStatus.Reversed,
        };
        return statusMapping[status];
    }
}

export enum FantasySelectionOrderStatus {
    Unknown = 0,
    Success = 1,
    ContestClosed = 10,
    PoolFull = 11,
    MemberSuspend = 20,
    AccountProblem = 22,
    TestAccountBettingDisabled = 23,
    HasDepositBonus = 24,
    BettingBudgetExceeded = 25,
    UserNotTestAccount = 26,
    InsufficientCredit = 30,
    StakeOverThanMaxBet = 31,
    StakeLowerThanMinBet = 32,
    GeneralFailure = 255,
}

export namespace FantasySelectionOrderStatus {
    export function fromPollux(status: PolluxFantasySelectionOrderStatus): FantasySelectionOrderStatus {
        const statusMapping: Record<PolluxFantasySelectionOrderStatus, FantasySelectionOrderStatus> = {
            [PolluxFantasySelectionOrderStatus.Unknown]: FantasySelectionOrderStatus.Unknown,
            [PolluxFantasySelectionOrderStatus.Success]: FantasySelectionOrderStatus.Success,
            [PolluxFantasySelectionOrderStatus.ContestClosed]: FantasySelectionOrderStatus.ContestClosed,
            [PolluxFantasySelectionOrderStatus.PoolFull]: FantasySelectionOrderStatus.PoolFull,
            [PolluxFantasySelectionOrderStatus.MemberSuspend]: FantasySelectionOrderStatus.MemberSuspend,
            [PolluxFantasySelectionOrderStatus.MemberClosed]: FantasySelectionOrderStatus.GeneralFailure,
            [PolluxFantasySelectionOrderStatus.AccountProblem]: FantasySelectionOrderStatus.AccountProblem,
            [PolluxFantasySelectionOrderStatus.TestAccountBettingDisabled]: FantasySelectionOrderStatus.TestAccountBettingDisabled,
            [PolluxFantasySelectionOrderStatus.HasDepositBonus]: FantasySelectionOrderStatus.HasDepositBonus,
            [PolluxFantasySelectionOrderStatus.BettingBudgetExceeded]: FantasySelectionOrderStatus.BettingBudgetExceeded,
            [PolluxFantasySelectionOrderStatus.UserNotTestAccount]: FantasySelectionOrderStatus.UserNotTestAccount,
            [PolluxFantasySelectionOrderStatus.InsufficientCredit]: FantasySelectionOrderStatus.InsufficientCredit,
            [PolluxFantasySelectionOrderStatus.StakeOverThanMaxBet]: FantasySelectionOrderStatus.StakeOverThanMaxBet,
            [PolluxFantasySelectionOrderStatus.StakeLowerThanMinBet]: FantasySelectionOrderStatus.StakeLowerThanMinBet,
            [PolluxFantasySelectionOrderStatus.GeneralFailure]: FantasySelectionOrderStatus.GeneralFailure,
        };
        return statusMapping[status] ?? FantasySelectionOrderStatus.GeneralFailure;
    }
}

export enum ContestStatus {
    Unknown,
    Running,
    Closed,
    PendingResult,
    Completed,
}

export namespace ContestStatus {
    export function fromPollux(status: PolluxContestStatus): ContestStatus {
        const statusMapping: Record<PolluxContestStatus, ContestStatus> = {
            [PolluxContestStatus.Unknown]: ContestStatus.Unknown,
            [PolluxContestStatus.Running]: ContestStatus.Running,
            [PolluxContestStatus.Closed]: ContestStatus.Closed,
            [PolluxContestStatus.PendingResult]: ContestStatus.PendingResult,
            [PolluxContestStatus.Completed]: ContestStatus.Completed,
        };
        return statusMapping[status] ?? ContestStatus.Unknown;
    }
}

export enum LeagueSize {
    Big = 'Big',
    Medium = 'Medium',
    Small = 'Small'
}

export namespace LeagueSize {
    const leagueSizeMapping: Record<EnumTournamentSizeType, LeagueSize> = {
        [EnumTournamentSizeType.Big]: LeagueSize.Big,
        [EnumTournamentSizeType.Medium]: LeagueSize.Medium,
        [EnumTournamentSizeType.Small]: LeagueSize.Small,
    };
    export function fromOddsApi(size: EnumTournamentSizeType): LeagueSize {
        return leagueSizeMapping[size];
    }
}

export enum RecommendType {
    Unknown = 0,
    BetCount = 1,
    Turnover = 2,
}

export namespace RecommendType {
    export function toRogue(recommendType: RecommendType): RogueRecommendType {
        const recommendTypeMapping: Record<RecommendType, RogueRecommendType> = {
            [RecommendType.Unknown]: 0,
            [RecommendType.BetCount]: 1,
            [RecommendType.Turnover]: 2,
        };
        return recommendTypeMapping[recommendType];
    }
}

export enum OddApiStatus {
    NO_TOKEN = 'NO_TOKEN',
    LOADED = 'LOADED',
    LOADING = 'LOADING',
    ERRORED = 'ERRORED',
}

export enum GroupTableColumn {
    Played = 'played',
    Won = 'won',
    Drawn = 'drawn',
    Lost = 'lost',
    GoalDifference = 'goalDifference',
    Points = 'points',
    Winner = 'winner',
}

export enum Country {
    Indonesia = 'Indonesia',
    Thailand = 'Thailand',
    Vietnam = 'Vietnam',
    Malaysia = 'Malaysia',
    Myanmar = 'Myanmar',
    Philippines = 'Philippines',
    Singapore = 'Singapore',
    Cambodia = 'Cambodia',
    Laos = 'Laos',
    EastTimor = 'Timor Leste',
    Brunei = 'Brunei',
    Argentina = 'Argentina',
    Portugal = 'Portugal',
    Croatia = 'Croatia',
    Spain = 'Spain',
    France = 'France',
    England = 'England',
    Brazil = 'Brazil',
    Netherlands = 'Netherlands',
    Belgium = 'Belgium',
    Germany = 'Germany',
    Morocco = 'Morocco',
    Colombia = 'Colombia',
    USA = 'USA',
    Mexico = 'Mexico',
    Uruguay = 'Uruguay',
    Switzerland = 'Switzerland',
    Japan = 'Japan',
    Senegal = 'Senegal',
    Iran = 'Iran',
    KoreaRepublic = 'Korea Republic',
    Ecuador = 'Ecuador',
    Austria = 'Austria',
    Australia = 'Australia',
    Canada = 'Canada',
    Norway = 'Norway',
    Panama = 'Panama',
    Algeria = 'Algeria',
    Egypt = 'Egypt',
    Scotland = 'Scotland',
    Paraguay = 'Paraguay',
    Tunisia = 'Tunisia',
    CoteDIvoire = 'Cote d Ivoire',
    Uzbekistan = 'Uzbekistan',
    Qatar = 'Qatar',
    SaudiArabia = 'Saudi Arabia',
    SouthAfrica = 'South Africa',
    Jordan = 'Jordan',
    CapeVerde = 'Cape Verde',
    Ghana = 'Ghana',
    NewZealand = 'New Zealand',
    Curacao = 'Curacao',
    Haiti = 'Haiti',
    Czechia = 'Czechia',
    BosniaHerzegovina = 'Bosnia Herzegovina',
    Turkiye = 'Turkiye',
    Sweden = 'Sweden',
    Iraq = 'Iraq',
    DRCongo = 'DR Congo',
}

export enum ResultTimeType {
    Unknown,
    FullTime,
    FirstHalf,
}

export enum TicketVoucherOptionStatus {
    Available = 'available',
    Selected = 'selected',
    SelectedByOtherTicket = 'selectedByOtherTicket',
    Unavailable = 'unavailable',
    UnavailableOddsInvalid = 'unavailableOddsInvalid',
    UnavailableStakeRange = 'unavailableStakeRange',
}

export enum MainPagePopup {
    TelegramBot = 'telegramBot',
    JackpotWinner = 'jackpotWinner',
    SingleModeBetSlipTutorial = 'singleModeBetSlipTutorial',
    BetBuilderTutorial = 'betBuilderTutorial',
    VoucherRedeem = 'voucherRedeem',
}

export enum FeatureEvent {
    Unknown = 0,
    SingleModeBetSlipTutorialCompleted = 1,
}

export namespace FeatureEvent {
    const featureEventPolluxMapping: Partial<Record<number, FeatureEvent>> = {
        [PolluxSportsFeatureEvent.Unknown]: FeatureEvent.Unknown,
        [PolluxSportsFeatureEvent.SingleModeBetSlipTutorialCompleted]: FeatureEvent.SingleModeBetSlipTutorialCompleted,
    };

    const polluxFeatureEventMapping: Record<FeatureEvent, PolluxSportsFeatureEvent> = {
        [FeatureEvent.Unknown]: PolluxSportsFeatureEvent.Unknown,
        [FeatureEvent.SingleModeBetSlipTutorialCompleted]: PolluxSportsFeatureEvent.SingleModeBetSlipTutorialCompleted,
    };

    export function fromPollux(event: number): FeatureEvent {
        return featureEventPolluxMapping[event] ?? FeatureEvent.Unknown;
    }

    export function toPollux(event: FeatureEvent): PolluxSportsFeatureEvent {
        return polluxFeatureEventMapping[event];
    }
}

export enum SingleModeBetSlipTutorialStep {
    ShowBetSlip = 0,
    HighlightLobbyOdds = 1,
}

export enum SingleModeBetSlipTutorialSpotlight {
    FirstLeagueOddsMock = 'firstLeagueOddsMock',
    OtherLeagueOddsMock = 'otherLeagueOddsMock',
}
